import React, { useState, useEffect, useRef, useCallback } from "react";
import "./styles.css";
import ReaderLab from "./ReaderLab";
import EpubTranslator from "./EpubTranslator";
import EchoMiniPlayer from "./EchoMiniPlayer";
import LiveBridgeOrganize from "./LiveBridgeOrganize";
import useLiveBridge from "./useLiveBridge";
import { useWonderWorld } from "./useWonderWorld";
import {
  downloadReportTxt,
  generateSummary,
  generateTrendReport,
  getCognitiveType,
  getCognitiveTypeLabel
} from "./WonderWorldReport";
import { loadProgress } from "./wonderWorldProgress";
import { worldOrder, worlds } from "./data/wonderWorldData";
import {
  STANDALONE_PRODUCT_ENTRY_PAGES,
  STANDALONE_PRODUCT_URLS,
  getRuntimeHostname,
  getStandaloneProductKey,
  isRootsBridgeDomain,
  pageBelongsToStandaloneProduct
} from "./lib/productRouting";
import WonderGlyphText from "./WonderGlyphText";
import WonderWorldViews from "./WonderWorldViews";
import rootsBridgeLogo from "./roots-bridge-logo-stacked.svg";
import APP_ZH_TEXT from "./appZh.generated";
const worldMeta = {
  space: { icon: "🚀", name: { en: "Space Exploration", fr: "Exploration Spatiale" } },
  wildlife: { icon: "🦁", name: { en: "Wildlife Adventure", fr: "Aventure Animalière" } },
  farm: { icon: "🚜", name: { en: "Farm Life", fr: "Vie à la Ferme" } },
  city: { icon: "🤖", name: { en: "Urban Sci-Fi", fr: "Sci-Fi Urbaine" } },
  magic: { icon: "✨", name: { en: "Magic Academy", fr: "Académie de Magie" } },
  zombie: { icon: "🌫️", name: { en: "Shadow Escape", fr: "Fuite dans la Brume" } }
};
const uiCopy = {
  en: {
    start_adventure: "Begin the Journey",
    for_parents: "For Parents",
    about_back: "Back",
    about_view_report: "Open Parent Report",
    feedback: "Share Feedback",
    back_home: "Back Home",

    adventure_map: "World Map",
    back_to_map: "Back to Map",
    progress: "Progress: {done}/{total}",
    stars: "⭐ Stars: {stars}",
    chapter: "{world} — Chapter {level}",
    locked_world: "Locked",
    unlocked_world: "Open",
    next_world: "Next World",
    replay_world: "Play This World Again",

    continue: "Continue",
    continue_story: "Continue the Story",
    make_choice: "Choose Your Path",
    checkpoint_complete: "Checkpoint cleared. The path forward is open.",
    no_question_found: "No challenge found here yet.",
    question_lost: "This challenge was lost. Returning to the story.",
    progress_restored: "Progress Restored",
    question_restored: "Challenge Restored",

    start_challenge: "Start Challenge",
    try_again: "Try Again",
    challenge_complete: "Challenge Complete",
    chapter_complete: "Chapter Complete",
    world_complete: "You completed {world}.",
    adventure_complete: "The Journey Continues ⭐",
    world_passed_message: "Amazing! You have passes this WORLD!",

    mission_summary: "Story Summary",
    what_you_unlocked: "What You Unlocked",
    earned_stars: "Stars Earned",
    final_ending_title: "You Are a Bridge Keeper",
    back_to_worlds: "Return to the Worlds",

    feedback_title: "Feedback",
    feedback_prompt: "How did this adventure feel?",
    feedback_note_label: "Anything you want to tell us?",
    feedback_note_placeholder: "Write your thoughts here...",
    feedback_send: "Send Feedback",
    feedback_pick_or_type: "Choose an option or leave a message.",
    feedback_mail_opened: "Your email app opened with a draft message.",
    loved_it: "Loved it",
    it_was_ok: "It was okay",
    not_fun: "Needs more fun",

    parent_report: "Parent Report",
    parent_no_data: "No session data yet.",
    parent_no_data_hint: "Complete one world first, then come back here.",
    latest_session: "Latest Session",
    latest_profile: "Latest Play Style",
    latest_narrative: "Session Reflection",
    overall_trend: "Overall Trend",
    recent_sessions: "Recent Sessions",
    clear_report_history: "Clear Report History",

    gentle_note_title: "A Gentle Note",
    gentle_note_body:
      "These observations are light reflections on how a child responded during play. They are not labels, tests, or fixed conclusions."
  },

  fr: {
    start_adventure: "Commencer le voyage",
    for_parents: "Pour les parents",
    about_back: "Retour",
    about_view_report: "Ouvrir le rapport parental",
    feedback: "Partager un avis",
    back_home: "Retour à l'accueil",

    adventure_map: "Carte des mondes",
    back_to_map: "Retour à la carte",
    progress: "Progression : {done}/{total}",
    stars: "⭐ Étoiles : {stars}",
    chapter: "{world} — Chapitre {level}",
    locked_world: "Verrouillé",
    unlocked_world: "Ouvert",
    next_world: "Monde suivant",
    replay_world: "Rejouer ce monde",

    continue: "Continuer",
    continue_story: "Continuer l'histoire",
    make_choice: "Choisis ton chemin",
    checkpoint_complete: "Point de passage validé. Le chemin s'ouvre.",
    no_question_found: "Aucun défi trouvé ici pour le moment.",
    question_lost: "Ce défi a été perdu. Retour à l'histoire.",
    progress_restored: "Progression restaurée",
    question_restored: "Défi restauré",

    start_challenge: "Commencer le défi",
    try_again: "Réessayer",
    challenge_complete: "Défi terminé",
    chapter_complete: "Chapitre terminé",
    world_complete: "Tu as terminé {world}.",
    adventure_complete: "Le voyage continue ⭐",
    world_passed_message: "Incroyable ! Tu as réussi ce MONDE !",

    mission_summary: "Résumé de l'histoire",
    what_you_unlocked: "Ce que tu as débloqué",
    earned_stars: "Étoiles gagnées",
    final_ending_title: "Tu es un Gardien du Pont",
    back_to_worlds: "Retour aux mondes",

    feedback_title: "Avis",
    feedback_prompt: "Comment as-tu trouvé cette aventure ?",
    feedback_note_label: "Veux-tu nous dire quelque chose ?",
    feedback_note_placeholder: "Écris ton message ici...",
    feedback_send: "Envoyer l'avis",
    feedback_pick_or_type: "Choisis une option ou laisse un message.",
    feedback_mail_opened: "Ton application e-mail s'est ouverte avec un brouillon.",
    loved_it: "J'ai adoré",
    it_was_ok: "C'était bien",
    not_fun: "Pas assez amusant",

    parent_report: "Rapport parental",
    parent_no_data: "Aucune donnée de session pour le moment.",
    parent_no_data_hint: "Terminez d'abord un monde, puis revenez ici.",
    latest_session: "Dernière session",
    latest_profile: "Style de jeu récent",
    latest_narrative: "Réflexion sur la session",
    overall_trend: "Tendance générale",
    recent_sessions: "Sessions récentes",
    clear_report_history: "Effacer l'historique du rapport",

    gentle_note_title: "Note bienveillante",
    gentle_note_body:
      "Ces observations sont de légères pistes de réflexion sur la manière dont un enfant a réagi pendant le jeu. Ce ne sont ni des étiquettes, ni un test, ni des conclusions définitives."
  }
};

const uiButtons = {
  en: {
    start: "Start",
    continue: "Continue",
    choose: "Choose",
    challenge: "Challenge",
    retry: "Try Again",
    next: "Next",
    map: "Map",
    home: "Home",
    report: "Report",
    feedback: "Feedback",
    replay: "Replay"
  },
  fr: {
    start: "Commencer",
    continue: "Continuer",
    choose: "Choisir",
    challenge: "Défi",
    retry: "Réessayer",
    next: "Suivant",
    map: "Carte",
    home: "Accueil",
    report: "Rapport",
    feedback: "Avis",
    replay: "Rejouer"
  }
};

const chapterIntroCopy = {
  space: {
    mapTitle: "Space Exploration",
    mapSubtitle: "Follow the first broken signal.",
    mapTag: "A silent planet. A sleeping reactor. The journey begins.",
    introTitle: "The First Light",
    introLines: [
      "Your ship has crash-landed on a distant alien world, and its engine is completely dead.",
      "A glowing guide named Lumi appears beside you and points toward an ancient signal hidden somewhere in the dark."
    ]
  },

  city: {
    mapTitle: "Urban Sci-Fi",
    mapSubtitle: "Enter the city that is losing power.",
    mapTag: "Lights flicker. Systems fail. Something deeper is wrong.",
    introTitle: "The Fading City",
    introLines: [
      "The restored reactor sends you to Neon Harbor, a futuristic city where the lights are going out one district at a time.",
      "Lumi believes the next fragment is hidden inside the mind of the city itself."
    ]
  },

  wildlife: {
    mapTitle: "Wildlife Adventure",
    mapSubtitle: "Follow the animals into silence.",
    mapTag: "The forest is uneasy, and the lake is waiting.",
    introTitle: "The Silent Reserve",
    introLines: [
      "A map from the city points toward a wildlife reserve where animals from every direction are gathering in strange patterns.",
      "Lumi senses that nature has noticed the broken Bridge long before anyone else."
    ]
  },

  farm: {
    mapTitle: "Farm Life",
    mapSubtitle: "Help the land remember how to grow.",
    mapTag: "The soil is tired, but the farm is still holding on.",
    introTitle: "The Quiet Fields",
    introLines: [
      "A message beneath the old tree sends you to a farm where the earth feels cold and the windmill barely turns.",
      "Something is wrong with the land, and the people working here are trying to hold everything together."
    ]
  },

  zombie: {
    mapTitle: "Shadow Escape",
    mapSubtitle: "Guide the lost through the gray mist.",
    mapTag: "The city is not full of monsters. It is full of people who have forgotten the way.",
    introTitle: "The Gray City",
    introLines: [
      "Beyond the fields, a heavy gray mist has swallowed the city streets and turned them into a maze of fear and confusion.",
      "Lumi discovers that the shadow does not simply attack — it makes people lose their sense of direction, memory, and belonging."
    ]
  },

  magic: {
    mapTitle: "Magic Academy",
    mapSubtitle: "Open the final gate.",
    mapTag: "The fragments are ready. The Bridge is waiting.",
    introTitle: "The Last Gate",
    introLines: [
      "With five fragments in hand, you arrive at a silent academy where the Wonder Bridge was once protected.",
      "If the final portal can be opened, the worlds may remember how to connect again."
    ]
  }
};

const worldCardCopy = {
  space: {
    title: "Space Exploration",
    blurb: "Trace the first signal through alien ruins."
  },
  city: {
    title: "Urban Sci-Fi",
    blurb: "Restore power in a city that is fading."
  },
  wildlife: {
    title: "Wildlife Adventure",
    blurb: "Find out why the animals are gathering."
  },
  farm: {
    title: "Farm Life",
    blurb: "Help a struggling farm wake the land."
  },
  zombie: {
    title: "Shadow Escape",
    blurb: "Lead survivors through the gray mist."
  },
  magic: {
    title: "Magic Academy",
    blurb: "Unlock the final secret of the Bridge."
  }
};

const chapterCopy = {
  space: {
    completionTitle: "The First Light Returns",
    completionBody:
      "The ancient reactor hums back to life, and blue light rushes through the ruins. A hidden signal rises into the sky, carrying the shape of a distant city losing power block by block. Lumi drifts beside you and whispers that this was never only about your crashed ship. The first fragment is awake, and the next world is already calling.",
    lumiLine:
      "We did not just fix a machine. We woke up the first memory of the Bridge.",
    nextHint:
      "Next destination: a city that has forgotten how to stay bright.",
    summaryTitle: "Chapter Complete: Space Exploration",
    summaryBody:
      "You followed the first signal into a broken alien ruin and helped restore an ancient reactor. In doing so, you recovered the first fragment of the Wonder Bridge and discovered that the trouble is spreading far beyond one world. This journey is no longer about escape. It is about connection."
  },

  city: {
    completionTitle: "A City Begins to Remember",
    completionBody:
      "The AI core stabilizes, and Neon Harbor lights up again in waves of electric blue. Screens across the control tower fill with maps, weather patterns, and animal routes bending in the same strange direction. The city was not the cause of the imbalance. It was only one of the first places to feel it. As the second fragment rises from the core, Lumi turns the wildlife map toward you.",
    lumiLine:
      "Machines can measure the damage. Nature can feel the pain first.",
    nextHint:
      "Next destination: a reserve where the animals are gathering in silence.",
    summaryTitle: "Chapter Complete: Urban Sci-Fi",
    summaryBody:
      "You entered a failing futuristic city and helped restore its energy core. The second fragment revealed that the problem is not limited to power systems or broken machines. The imbalance is spreading across worlds and into living ecosystems. The trail now leads into the wild."
  },

  wildlife: {
    completionTitle: "The Forest Was Listening",
    completionBody:
      "Beneath the roots of the old lakeside tree, the third fragment glows like a sleeping seed finally touched by sunlight. Around the lake, animals from different habitats begin to settle and drift apart, no longer frozen by the same uneasy waiting. Carved into a hidden stone, you find a message: 'When the ground forgets how to grow, seek the hands that still care for it.' Lumi reads it twice before looking toward the distant fields.",
    lumiLine:
      "The forest did not speak with words. It spoke by waiting for someone to notice.",
    nextHint:
      "Next destination: a farm where the land is growing quiet.",
    summaryTitle: "Chapter Complete: Wildlife Adventure",
    summaryBody:
      "You tracked strange animal migrations through the reserve and discovered that nature itself was reacting to the broken Bridge. At the silent lake, you recovered the third fragment and uncovered a clue pointing toward farmland and human care. The journey now moves from wilderness into everyday life."
  },

  farm: {
    completionTitle: "The Land Takes a Breath",
    completionBody:
      "The old windmill groans, then turns with steady strength, and a warm pulse moves through the fields. The fourth fragment shines out from the farm like a small harvest star. But before the feeling of relief can settle, everyone looks up. Far beyond the orchard, a gray column of mist rises over the city. Families who fled there were right to be afraid. Something worse is spreading now.",
    lumiLine:
      "We helped the land remember how to breathe. Now we have to help people remember how to find their way back.",
    nextHint:
      "Next destination: a city swallowed by gray shadow.",
    summaryTitle: "Chapter Complete: Farm Life",
    summaryBody:
      "You helped care for animals, organize supplies, and restore life to a struggling farm. The fourth fragment awakened when the land finally responded, but the victory was short-lived. Refugees from the city brought news of a gray mist that steals direction, memory, and calm. The next chapter is the most dangerous yet."
  },

  zombie: {
    completionTitle: "Through the Gray Mist",
    completionBody:
      "The last bus pulls away from the school, carrying families out of the fog. Behind you, the gray mist shifts and curls, not like a monster defeated, but like an emptiness losing its grip. In the bus yard, the fifth fragment and an old academy crest are found together, as if they had been waiting for the same hands to reach them. Lumi studies the crest and finally understands where the road ends.",
    lumiLine:
      "This shadow was never only trying to scare people. It was trying to make them forget who they were, and where they belonged.",
    nextHint:
      "Next destination: the magic academy where the Wonder Bridge was once guarded.",
    summaryTitle: "Chapter Complete: Shadow Escape",
    summaryBody:
      "You entered the gray city, protected survivors, and guided them toward safety. The fifth fragment revealed that the spreading shadow is less like a virus and more like a growing emptiness that severs people from memory and belonging. With the academy crest in hand, the path to the final world is finally clear."
  },

  magic: {
    completionTitle: "The Bridge Wakes",
    completionBody:
      "Inside the academy portal, the five fragments blaze together, and the missing sixth awakens in response to you. Not hidden in a vault, not lost in a ruin, but waiting in the one who chose to listen, explore, protect, and reconnect. Light pours through the portal ring. Across every world, systems restart, fields soften, forests settle, and the gray mist begins to fold back into silence. The Wonder Bridge lives again.",
    lumiLine:
      "The final fragment was never an object. It was the choice to connect worlds that had begun to forget each other.",
    nextHint:
      "The story is complete — but the Bridge is open again, and more journeys can begin.",
    summaryTitle: "Chapter Complete: Magic Academy",
    summaryBody:
      "You reached the silent academy and uncovered the truth behind the Wonder Bridge. The final fragment was not something to collect, but something to awaken through courage, care, and connection. With the Bridge restored, all six worlds begin to remember one another, and the journey becomes something larger than survival: it becomes stewardship."
  },

  finalEnding: {
    title: "You Are a Bridge Keeper",
    body:
      "You followed broken signals across six worlds. You restored power, listened to the wild, cared for the land, protected the lost, and reopened the final gate. The Wonder Bridge is alive again because you chose not only to solve problems, but to reconnect what had been drifting apart. The worlds are awake. Their stories are no longer separate. And now, they remember you.",
    lumiLine:
      "Thank you, Bridge Keeper. When worlds begin to drift again, the Bridge will know how to find you.",
    closingTag:
      "More stories are waiting on the other side."
  }
};

const bossCopy = {
  space: {
    title: "Reactor Lock",
    challengeLine:
      "The ancient reactor is sealed behind a final control puzzle.",
    objective:
      "Restore power to the alien ruins and awaken the first fragment.",
    successTitle: "Reactor Restored",
    successBody:
      "The reactor roars back to life, and blue energy floods the ruins. Your ship systems begin to recover, but more importantly, the first fragment of the Wonder Bridge finally wakes. A hidden signal rises into the sky, pointing toward a city losing power far away.",
    victoryHint:
      "A new destination has appeared: Neon Harbor."
  },

  city: {
    title: "Core Stabilizer",
    challengeLine:
      "The city AI core is unstable, and the whole grid depends on your next move.",
    objective:
      "Balance the system and bring Neon Harbor back online.",
    successTitle: "Core Stabilized",
    successBody:
      "The energy core steadies, and light spreads through the city block by block. But the system logs reveal something bigger: the trouble is not trapped inside the city. Wildlife routes and natural patterns are shifting too, as if the worlds are pulling away from one another.",
    victoryHint:
      "A wildlife reserve has been marked as your next route."
  },

  wildlife: {
    title: "Lakewatch Trial",
    challengeLine:
      "The animals have gathered at the lake for a reason. You must understand the pattern before it disappears.",
    objective:
      "Read the signs of the reserve and uncover the hidden fragment.",
    successTitle: "The Lake Answered",
    successBody:
      "The waiting ends. Beneath the roots of the old tree, the third fragment begins to glow like a waking seed. The animals slowly settle, and a message carved in stone points you toward the next truth: when the land begins to fail, look for the people still caring for it.",
    victoryHint:
      "The trail now leads to a struggling farm."
  },

  farm: {
    title: "Harvest Engine",
    challengeLine:
      "The farm can still survive, but only if you restore order before everything gives out.",
    objective:
      "Help the land recover and secure the fourth fragment.",
    successTitle: "The Fields Breathe Again",
    successBody:
      "The windmill turns, the fields answer, and the fourth fragment shines out over the farm like a harvest star. For one moment, everything feels steady again. Then the horizon darkens. A gray column of mist rises over the city, and everyone knows the real crisis has arrived.",
    victoryHint:
      "The next chapter begins inside the shadowed city."
  },

  zombie: {
    title: "Last Bus Plan",
    challengeLine:
      "The shelter cannot hold forever. You need one clear plan to get people out.",
    objective:
      "Guide the survivors through the gray mist and secure safe escape.",
    successTitle: "Escape Secured",
    successBody:
      "The final bus pulls away, carrying families out of danger. The mist twists behind you, weaker now, as if losing its hold on the people it tried to erase. In the bus yard, you recover the fifth fragment and an old academy crest — the clearest sign yet that the final answer lies elsewhere.",
    victoryHint:
      "The crest points to the Magic Academy."
  },

  magic: {
    title: "Portal Ring",
    challengeLine:
      "Five fragments are ready, but the final gate will only open for the one who can reconnect the worlds.",
    objective:
      "Activate the portal and restore the Wonder Bridge.",
    successTitle: "The Bridge Wakes",
    successBody:
      "Light races through the portal ring as the final fragment awakens in response to you. Across every world, systems recover, forests settle, fields soften, and the gray mist begins to fold back into silence. The Wonder Bridge is alive again — not because you collected power, but because you restored connection.",
    victoryHint:
      "You have become a Bridge Keeper."
  }
};

const bossMiniCopy = {
  space: {
    title: "Reactor Lock",
    objective: "Restart the ancient reactor.",
    success: "The first fragment is awake."
  },
  city: {
    title: "Core Stabilizer",
    objective: "Balance the city's failing core.",
    success: "Neon Harbor shines again."
  },
  wildlife: {
    title: "Lakewatch Trial",
    objective: "Read the pattern at the silent lake.",
    success: "The forest reveals its fragment."
  },
  farm: {
    title: "Harvest Engine",
    objective: "Restore order to the struggling farm.",
    success: "The land begins to breathe again."
  },
  zombie: {
    title: "Last Bus Plan",
    objective: "Lead the survivors to safety.",
    success: "The fifth fragment is secured."
  },
  magic: {
    title: "Portal Ring",
    objective: "Open the final gate.",
    success: "The Wonder Bridge lives again."
  }
};

const I18N = {
  en: {
    adventure_badge: "A Story Puzzle Adventure",
    game_title: "Wonder World",
    home_studio_title: "R&B Studio",
    home_welcome_title: "Welcome to Wonder World!",
    home_welcome_subtitle: "This isn't homework. It's an adventure.",
    home_hero_subtitle: "Every choice opens a new world.",
    home_chip_story: "Story Choices",
    home_chip_puzzles: "Boss Puzzles",
    home_chip_rewards: "Stars & Unlocks",
    home_reader_lab_badge: "PageTone",
    home_reader_lab_note:
      "Upload a book (EPUB, MOBI, PDF, TXT, and DOCX supported) and move through it your way - read on screen, switch to audio, follow chapter by chapter, and come back right where you left off.",
    home_reader_lab_note_followup:
      "PageTone supports English, Chinese, and French, with no app to install and no account to create.",
    home_reader_lab_note_close: "Just open, play, and keep going.",
    echo_tagline: "Your books, ready to read or listen.",
    home_reader_lab_cta: "Open PageTone",
    home_reader_lab_empty: "Load a book in PageTone first, then you can control playback right from the home page.",
    home_reader_lab_progress: "Progress",
    home_intro_line1: "Explore space, forests, cities, magic, and more.",
    home_intro_line2: "Solve puzzles, earn stars, and unlock new worlds.",
    home_intro_line3: "Every choice can change where the story goes.",
    home_subtitle: "Jump into magical worlds, solve puzzles, and unlock the next adventure.",
    start_adventure: "Start Adventure",
    for_parents: "For Parents",
    about_title: "The Idea Behind Wonder World",
    about_back: "Back",
    about_view_report: "View Report",
    parent_report: "Parent Report",
    feedback: "Feedback",
    feedback_title: "Feedback",
    feedback_prompt: "Did you enjoy this adventure?",
    feedback_note_label: "I wanna say something:",
    feedback_note_placeholder: "Type your message here...",
    feedback_send: "Send to rootsandbridge@outlook.com",
    feedback_pick_or_type: "Please choose an option or leave a message.",
    feedback_mail_opened: "Your email app is opening with the feedback draft.",
    adventure_map: "Adventure Map",
    progress: "Progress: {done}/{total}",
    stars: "⭐ Stars: {stars}",
    chapter: "{world} — Chapter {level}",
    back_to_map: "Back to Map",
    no_question_found: "No question found for this world yet.",
    checkpoint_complete: "Checkpoint cleared. Keep moving toward the boss!",
    question_lost: "Question data was lost. Returning to story.",
    progress_restored: "Progress Restored",
    story_restore_hint:
      "We could not fully restore this story step after refresh. Please return to the map and restart this world.",
    question_restored: "Question Restored",
    question_restore_hint:
      "This question expired after refresh. Return to map and continue your adventure.",
    nice_work: "Nice work!",
    keep_going: "Keep going!",
    answer_helped: "Your answer helped the mission move forward.",
    learned_continue: "You learned something useful. Now continue the adventure.",
    continue: "Continue",
    adventure_complete: "Adventure Complete ⭐",
    world_complete: "You completed the {world} world.",
    world_passed_message: "Amazing! You have passes this WORLD!",
    loved_it: "Loved it",
    it_was_ok: "It was ok",
    not_fun: "Not fun",
    parent_no_data: "No session data yet.",
    parent_no_data_hint: "Let your child complete at least one world, then come back here.",
    latest_session: "Latest Session",
    world: "World",
    played: "Played",
    session_duration: "Session Duration",
    minutes: "minutes",
    questions_answered: "Questions Answered",
    accuracy: "Accuracy",
    stars_earned: "Stars Earned",
    latest_profile: "Latest Cognitive Profile",
    type: "Type",
    exploration: "Exploration",
    analysis: "Analysis",
    risk: "Risk",
    curiosity: "Curiosity",
    care_responsibility: "Care / Responsibility",
    logic_preference: "Logic Preference",
    latest_narrative: "Latest Session Narrative",
    overall_trend: "Overall Trend",
    profile_type: "Profile Type",
    average_accuracy: "Average Accuracy",
    total_sessions: "Total Sessions",
    total_stars: "Total Stars",
    recent_sessions: "Recent Sessions",
    accuracy_inline: "accuracy",
    clear_report_history: "Clear Report History",
    back_home: "Back Home",
    clear_confirm_title: "Are you sure you want to delete all report records?",
    clear_confirm_text: "This action will clear your Parent Report history.",
    clear_yes_download: "Yes, and download the report",
    clear_no_delete_anyway: "No, delete it anyway",
    cancel: "Cancel",
    report_downloaded_deleted: "Report downloaded and history deleted.",
    history_deleted: "History deleted.",
    report_file_title: "Wonder World Parent Report",
    generated_at: "Generated At",
    no_session_report_data: "No session data is currently available.",
    latest_cognitive_profile: "Latest Cognitive Profile",
    latest_session_narrative: "Latest Session Narrative",
    recommendation: "Recommendation",
    no_changes_made: "No changes were made.",
    home_aria: "Main Menu",
    home_hub_intro: "Choose a space to enter.",
    home_hub_world_desc: "Story worlds, choices, puzzles, and new adventures begin here.",
    home_hub_world_cta: "Open Wonder World",
    home_hub_pagetone_desc:
      "Upload a book, read or listen, and continue from where you left off.",
    home_hub_pagetone_upload_title: "Upload a book",
    home_hub_pagetone_upload_hint: "EPUB, MOBI, PDF, TXT, DOCX, and DOC",
    home_hub_pagetone_cta: "Open PageTone",
    home_hub_livebridge_desc:
      "A free-first English and Chinese companion screen with live capture, lightweight translation, and local organize tools.",
    home_hub_livebridge_cta: "Open LiveBridge",
    livebridge_try_cta: "Try the prototype",
    livebridge_input_cta: "Open Input page",
    livebridge_viewer_cta: "Open Viewer page",
    livebridge_input_title: "LiveBridge Input",
    livebridge_viewer_title: "LiveBridge Viewer",
    livebridge_tagline: "A calm bilingual companion screen for family conversations.",
    livebridge_short_desc:
      "LiveBridge helps families capture full English or Chinese lines, lightly fill the other side when needed, and keep a simple private viewer open during a call.",
    livebridge_how_title: "How it works",
    livebridge_step_speak: "Capture full English or Chinese lines",
    livebridge_step_translate: "Fill the other side or organize the text",
    livebridge_step_view: "Keep it visible on a private page",
    livebridge_usecase_title: "Built for family calls",
    livebridge_usecase_body:
      "LiveBridge serves as a companion screen during family video calls, especially when a child mainly speaks English and grandparents mainly read Chinese. It keeps full lines visible and adds light bilingual support when needed.",
    livebridge_access_title: "Prototype access",
    livebridge_input_intro:
      "Use this page on the speaking side. Capture full English or Chinese speech when possible, keep the full draft on screen, then send it as-is or adjust it before sending. If one side is left blank, LiveBridge will try a lightweight translation before it appears on the shared screen.",
    livebridge_viewer_intro:
      "The Viewer is a calm subtitle-style page for grandparents. It stays simple, large, and readable during a live call.",
    livebridge_room_label: "Shared room",
    livebridge_room_value: "Main stage",
    livebridge_name_label: "Speaker name",
    livebridge_name_placeholder: "Optional name",
    livebridge_message_label: "Manual English line",
    livebridge_message_placeholder: "Type an English line for this demo...",
    livebridge_chinese_label: "Manual Chinese line",
    livebridge_chinese_placeholder: "Add the matching Chinese line for this demo...",
    livebridge_send: "Send current line",
    livebridge_sending: "Sending / filling...",
    livebridge_fill_missing: "Fill Missing Side",
    livebridge_clear: "Clear",
    livebridge_sent: "Message sent.",
    livebridge_fill_missing_ready: "The missing side is ready.",
    livebridge_fill_missing_empty: "Add one English or Chinese line first.",
    livebridge_fill_missing_full:
      "Both sides are already filled. Clear one side or edit before sending.",
    livebridge_fill_missing_failed: "Could not fill the missing side right now.",
    livebridge_start_listening_en: "Listen in English",
    livebridge_start_listening_zh: "Listen in Chinese",
    livebridge_stop_listening: "Stop Listening",
    livebridge_send_test: "Send Test Message",
    livebridge_new_session: "Start New Session",
    livebridge_latest_english: "Latest English",
    livebridge_latest_chinese: "Latest Chinese",
    livebridge_latest_output: "Latest output",
    livebridge_recent_lines: "Recent lines",
    livebridge_status_idle: "Idle",
    livebridge_status_listening: "Listening",
    livebridge_status_processing: "Processing",
    livebridge_status_done: "Done",
    livebridge_status_error: "Error",
    livebridge_status_connected: "Connected",
    livebridge_status_disconnected: "Disconnected",
    livebridge_status_syncing: "Syncing",
    livebridge_status_private: "Private viewer",
    livebridge_recording_timer: "Recording",
    livebridge_listening_english: "Currently listening for English.",
    livebridge_listening_chinese: "Currently listening for Chinese.",
    livebridge_speech_note:
      "If browser speech capture is unavailable, this MVP still works with test messages and manual English and Chinese drafts. Captured text stays in the draft area, and LiveBridge can try a lightweight fill for the missing side before sending.",
    livebridge_speech_ready: "Speech capture is available in this browser for English or Chinese.",
    livebridge_speech_unavailable: "Speech capture is not available in this browser yet.",
    livebridge_session_reset: "A fresh session is ready.",
    livebridge_password_label: "Viewer password",
    livebridge_password_placeholder: "Enter password",
    livebridge_unlock: "Unlock Viewer",
    livebridge_lock: "Lock Viewer",
    livebridge_viewer_waiting: "Waiting for messages...",
    livebridge_viewer_empty: "No messages yet. Send one from the Input page.",
    livebridge_last_updated: "Last updated",
    livebridge_session_note: "Viewer access stays only in this browser session.",
    livebridge_draft_note:
      "Drafts stay on this device, and live capture now keeps the full line in the draft area so you can review it before sending.",
    livebridge_preview_title: "Current bilingual preview",
    livebridge_preview_note:
      "Live keeps a paired preview warm and updates the missing side after a short delay.",
    livebridge_preview_empty: "The current live line will appear here.",
    livebridge_preview_badge_live: "Live",
    livebridge_preview_badge_ready: "Ready",
    home_hub_coming_desc: "Something new for stories, voices, and young minds is on the way.",
    home_hub_coming_alias: "",
    livebridge_title: "LiveBridge",
    livebridge_alias: "瞬言",
    livebridge_intro:
      "LiveBridge is a companion screen for family conversations, especially when children speak English and grandparents read Chinese during a call.",
    livebridge_note:
      "This MVP keeps things intentionally practical: one shared Input, one password-protected Viewer, full live draft capture, lightweight bilingual fill, and a free local organize mode for turning speech into usable notes.",
    livebridge_status: "Free-first MVP ready.",
    livebridge_mode_live: "Live",
    livebridge_mode_organize: "Organize",
    livebridge_mode_intro: "Choose the mode before you begin.",
    livebridge_mode_live_note:
      "Live keeps a shared bilingual draft and sends it to a private viewer.",
    livebridge_mode_organize_note:
      "Organize turns speech or pasted text into clear notes you can keep and reuse.",
    livebridge_organize_intro:
      "Free local mode: capture English or Chinese speech in your browser and turn it into a transcript, cleaned text, structured notes, title, summary, key points, and action items.",
    livebridge_source_language: "Source language",
    livebridge_target_language: "Translate to",
    livebridge_language_english: "English",
    livebridge_language_chinese: "Chinese",
    livebridge_record_start: "Start Listening",
    livebridge_record_stop: "Stop",
    livebridge_recording_active: "Listening for speech now.",
    livebridge_recording_stopping: "Stopping listening…",
    livebridge_organize_button: "Organize",
    livebridge_live_preview: "Live transcript preview",
    livebridge_live_preview_empty: "Your speech transcript will appear here.",
    livebridge_use_transcript_input: "Use Transcript as Input",
    livebridge_paste_text: "Paste text",
    livebridge_paste_placeholder: "Paste speech notes or raw transcript here...",
    livebridge_organize_empty: "No organized text yet.",
    livebridge_result_placeholder: "Record your voice or paste text to begin.",
    livebridge_organize_processing: "Organizing your text…",
    livebridge_organize_ready: "Organized result ready.",
    livebridge_translation_ready: "Le résultat organisé est prêt, avec une traduction légère ajoutée.",
    livebridge_translation_partial:
      "Le résultat organisé est prêt. La traduction a utilisé le mode bilingue léger.",
    livebridge_translation_failed:
      "Le résultat organisé est prêt. La traduction reste optionnelle dans ce MVP et peut manquer sur cette tentative.",
    livebridge_organize_mock_ready: "Mock organized result ready.",
    livebridge_organize_failed: "Organize request failed.",
    livebridge_organize_missing_text: "Add or capture some text before organizing.",
    livebridge_result_title: "Title",
    livebridge_result_summary: "Summary",
    livebridge_action_copy: "Copy",
    livebridge_action_download: "Download TXT",
    livebridge_action_retry: "Retry",
    livebridge_tab_transcript: "Transcript",
    livebridge_tab_cleaned: "Cleaned",
    livebridge_tab_structured: "Structured",
    livebridge_tab_translation: "Translation",
    livebridge_result_tabs: "Result tabs",
    livebridge_translation_placeholder:
      "Aucune traduction n’a été ajoutée pour cette tentative. Le résultat organisé ci-dessus reste prêt à l’emploi.",
    livebridge_key_points: "Key Points",
    livebridge_action_items: "Action Items",
    livebridge_copy_done: "Copied to clipboard.",
    livebridge_copy_failed: "Copy failed.",
    livebridge_download_ready: "TXT download is ready."
  },
  fr: {
    adventure_badge: "Une aventure d'histoires et d'énigmes",
    game_title: "Wonder World",
    home_studio_title: "R&B Studio",
    home_welcome_title: "Bienvenue dans Wonder World !",
    home_welcome_subtitle: "Ce n'est pas un devoir. C'est une aventure.",
    home_hero_subtitle: "Chaque choix ouvre un nouveau monde.",
    home_chip_story: "Choix d'histoire",
    home_chip_puzzles: "Défis boss",
    home_chip_rewards: "Étoiles et mondes",
    home_reader_lab_badge: "PageTone",
    home_reader_lab_note:
      "Importez un livre (EPUB, MOBI, PDF, TXT et DOCX pris en charge) et parcourez-le à votre rythme - lisez a l'ecran, passez a l'audio, avancez chapitre par chapitre, puis revenez exactement la ou vous vous etiez arrete.",
    home_reader_lab_note_followup:
      "PageTone prend en charge l'anglais, le chinois et le francais, sans application a installer ni compte a creer.",
    home_reader_lab_note_close: "Ouvrez, lancez, continuez.",
    echo_tagline: "Vos livres, prets a lire ou a ecouter.",
    home_reader_lab_cta: "Ouvrir PageTone",
    home_reader_lab_empty: "Chargez d'abord un livre dans PageTone, puis vous pourrez contrôler la lecture directement depuis l'accueil.",
    home_reader_lab_progress: "Progression",
    home_intro_line1: "Explore l'espace, les forêts, les villes, la magie, et bien plus encore.",
    home_intro_line2: "Résous des énigmes, gagne des étoiles et débloque de nouveaux mondes.",
    home_intro_line3: "Chaque choix peut changer le cours de ton histoire.",
    home_subtitle:
      "Plonge dans des mondes magiques, résous des énigmes et débloque la prochaine aventure.",
    start_adventure: "Commencer l'aventure",
    for_parents: "Pour les parents",
    about_title: "L'idée derrière Wonder World",
    about_back: "Retour",
    about_view_report: "Voir le rapport",
    parent_report: "Rapport parental",
    feedback: "Avis",
    feedback_title: "Avis",
    feedback_prompt: "As-tu aimé cette aventure ?",
    feedback_note_label: "I wanna say something:",
    feedback_note_placeholder: "Écrivez votre message ici...",
    feedback_send: "Envoyer à rootsandbridge@outlook.com",
    feedback_pick_or_type: "Veuillez choisir une option ou laisser un message.",
    feedback_mail_opened: "Votre application e-mail s'ouvre avec le brouillon du message.",
    adventure_map: "Carte d'Aventure",
    progress: "Progression : {done}/{total}",
    stars: "⭐ Étoiles : {stars}",
    chapter: "{world} — Chapitre {level}",
    back_to_map: "Retour à la carte",
    no_question_found: "Aucune question trouvée pour ce monde pour le moment.",
    checkpoint_complete: "Point de contrôle validé. Continue vers le boss !",
    question_lost: "La question a été perdue. Retour à l'histoire.",
    progress_restored: "Progression restaurée",
    story_restore_hint:
      "Nous n'avons pas pu restaurer complètement cette étape après l'actualisation. Revenez à la carte et redémarrez ce monde.",
    question_restored: "Question restaurée",
    question_restore_hint:
      "Cette question a expiré après actualisation. Revenez à la carte et continuez l'aventure.",
    nice_work: "Bien joué !",
    keep_going: "Continue !",
    answer_helped: "Ta réponse a aidé la mission à avancer.",
    learned_continue: "Tu as appris quelque chose d'utile. Continue maintenant l'aventure.",
    continue: "Continuer",
    adventure_complete: "Aventure terminée ⭐",
    world_complete: "Tu as terminé le monde {world}.",
    world_passed_message: "Incroyable ! Tu as réussi ce MONDE !",
    loved_it: "J'ai adoré",
    it_was_ok: "C'était correct",
    not_fun: "Pas amusant",
    parent_no_data: "Aucune donnée de session disponible pour le moment.",
    parent_no_data_hint:
      "Laissez votre enfant terminer au moins un monde, puis revenez consulter ce rapport.",
    latest_session: "Dernière session de jeu",
    world: "Monde",
    played: "Date",
    session_duration: "Durée de la session",
    minutes: "minutes",
    questions_answered: "Questions résolues",
    accuracy: "Taux de réussite",
    stars_earned: "Étoiles obtenues",
    latest_profile: "Profil cognitif récent",
    type: "Type",
    exploration: "Exploration",
    analysis: "Analyse",
    risk: "Risque",
    curiosity: "Curiosité",
    care_responsibility: "Soin / Responsabilité",
    logic_preference: "Préférence logique",
    latest_narrative: "Analyse narrative de la session",
    overall_trend: "Tendance générale",
    profile_type: "Profil dominant",
    average_accuracy: "Taux de réussite moyen",
    total_sessions: "Nombre total de sessions",
    total_stars: "Nombre total d'étoiles",
    recent_sessions: "Sessions récentes",
    accuracy_inline: "de réussite",
    clear_report_history: "Effacer l'historique du rapport",
    back_home: "Retour à l'accueil",
    clear_confirm_title: "Voulez-vous vraiment supprimer tous les enregistrements du rapport ?",
    clear_confirm_text: "Cette action effacera l'historique du Rapport Parent.",
    clear_yes_download: "Oui, et télécharger le rapport",
    clear_no_delete_anyway: "Non, le supprimer quand même",
    cancel: "Annuler",
    report_downloaded_deleted: "Rapport téléchargé et historique supprimé.",
    history_deleted: "Historique supprimé.",
    report_file_title: "Rapport parental Wonder World",
    generated_at: "Généré le",
    no_session_report_data: "Aucune donnée de session n'est disponible actuellement.",
    latest_cognitive_profile: "Profil cognitif récent",
    latest_session_narrative: "Analyse narrative de la session",
    recommendation: "Recommandation",
    no_changes_made: "Aucune modification n'a été effectuée.",
    home_aria: "Menu principal",
    home_hub_intro: "Choisissez un espace à ouvrir.",
    home_hub_world_desc:
      "Les mondes d'histoire, les choix, les énigmes et les nouvelles aventures commencent ici.",
    home_hub_world_cta: "Ouvrir Wonder World",
    home_hub_pagetone_desc:
      "Importez un livre, lisez ou écoutez, puis reprenez exactement là où vous vous êtes arrêté.",
    home_hub_pagetone_upload_title: "Importer un livre",
    home_hub_pagetone_upload_hint: "EPUB, MOBI, PDF, TXT, DOCX et DOC",
    home_hub_pagetone_cta: "Ouvrir PageTone",
    home_hub_livebridge_desc:
      "Un écran compagnon anglais-chinois, gratuit d’abord, avec capture en direct, traduction légère et outils d’organisation locaux.",
    home_hub_livebridge_cta: "Ouvrir LiveBridge",
    livebridge_try_cta: "Essayer le prototype",
    livebridge_input_cta: "Ouvrir la page Input",
    livebridge_viewer_cta: "Ouvrir la page Viewer",
    livebridge_input_title: "LiveBridge Input",
    livebridge_viewer_title: "LiveBridge Viewer",
    livebridge_tagline: "Un écran compagnon bilingue et calme pour les conversations familiales.",
    livebridge_short_desc:
      "LiveBridge aide les familles à capturer des phrases complètes en anglais ou en chinois, à compléter légèrement l’autre côté si besoin, et à garder un Viewer privé ouvert pendant l’appel.",
    livebridge_how_title: "Comment cela fonctionne",
    livebridge_step_speak: "Capturer des phrases complètes en anglais ou en chinois",
    livebridge_step_translate: "Compléter l’autre côté ou organiser le texte",
    livebridge_step_view: "Le garder visible sur une page privée",
    livebridge_usecase_title: "Pensé pour les appels familiaux",
    livebridge_usecase_body:
      "LiveBridge sert d’écran compagnon pendant les appels vidéo familiaux, surtout quand un enfant parle surtout anglais et que les grands-parents lisent surtout le chinois. Il garde les lignes complètes visibles et ajoute un léger soutien bilingue si nécessaire.",
    livebridge_access_title: "Accès au prototype",
    livebridge_input_intro:
      "Utilisez cette page du côté qui parle. Capturez si possible une phrase complète en anglais ou en chinois, gardez le brouillon complet à l’écran, puis envoyez-le tel quel ou ajustez-le avant l’envoi. Si un côté reste vide, LiveBridge tentera une traduction légère avant l’affichage.",
    livebridge_viewer_intro:
      "Le Viewer est une page de sous-titres calme et lisible pour les grands-parents pendant un appel vidéo.",
    livebridge_room_label: "Salle partagée",
    livebridge_room_value: "Scène principale",
    livebridge_name_label: "Nom du locuteur",
    livebridge_name_placeholder: "Nom facultatif",
    livebridge_message_label: "Ligne anglaise manuelle",
    livebridge_message_placeholder: "Tapez une ligne anglaise pour cette démo...",
    livebridge_chinese_label: "Ligne chinoise manuelle",
    livebridge_chinese_placeholder: "Ajoutez la ligne chinoise correspondante pour cette démo...",
    livebridge_send: "Envoyer la ligne",
    livebridge_sending: "Envoi / complément...",
    livebridge_fill_missing: "Compléter l’autre côté",
    livebridge_clear: "Effacer",
    livebridge_sent: "Message envoyé.",
    livebridge_fill_missing_ready: "Le côté manquant est prêt.",
    livebridge_fill_missing_empty: "Ajoutez d’abord une ligne en anglais ou en chinois.",
    livebridge_fill_missing_full:
      "Les deux côtés sont déjà remplis. Effacez-en un ou modifiez avant l’envoi.",
    livebridge_fill_missing_failed:
      "Impossible de compléter le côté manquant pour le moment.",
    livebridge_start_listening_en: "Écouter en anglais",
    livebridge_start_listening_zh: "Écouter en chinois",
    livebridge_stop_listening: "Arrêter l’écoute",
    livebridge_send_test: "Envoyer un message test",
    livebridge_new_session: "Nouvelle session",
    livebridge_latest_english: "Dernier anglais",
    livebridge_latest_chinese: "Dernier chinois",
    livebridge_latest_output: "Dernière sortie",
    livebridge_recent_lines: "Lignes récentes",
    livebridge_status_idle: "Inactif",
    livebridge_status_listening: "Écoute",
    livebridge_status_processing: "Traitement",
    livebridge_status_done: "Prêt",
    livebridge_status_error: "Erreur",
    livebridge_status_connected: "Connecté",
    livebridge_status_disconnected: "Déconnecté",
    livebridge_status_syncing: "Synchronisation",
    livebridge_status_private: "Viewer privé",
    livebridge_recording_timer: "Enregistrement",
    livebridge_listening_english: "Écoute en anglais.",
    livebridge_listening_chinese: "Écoute en chinois.",
    livebridge_speech_note:
      "Si la capture vocale du navigateur n’est pas disponible, ce MVP fonctionne quand même avec des messages test et des brouillons manuels en anglais et en chinois. Le texte capturé reste dans le brouillon, et LiveBridge peut tenter un léger complément du côté manquant avant l’envoi.",
    livebridge_speech_ready: "La capture vocale est disponible dans ce navigateur pour l’anglais ou le chinois.",
    livebridge_speech_unavailable: "La capture vocale n’est pas encore disponible dans ce navigateur.",
    livebridge_session_reset: "Une nouvelle session est prête.",
    livebridge_password_label: "Mot de passe Viewer",
    livebridge_password_placeholder: "Saisissez le mot de passe",
    livebridge_unlock: "Déverrouiller le Viewer",
    livebridge_lock: "Verrouiller le Viewer",
    livebridge_viewer_waiting: "En attente de messages...",
    livebridge_viewer_empty: "Aucun message pour le moment. Envoyez-en un depuis la page Input.",
    livebridge_last_updated: "Dernière mise à jour",
    livebridge_session_note: "L’accès Viewer reste uniquement dans cette session du navigateur.",
    livebridge_draft_note:
      "Les brouillons restent sur cet appareil, et la capture live conserve maintenant la phrase complète dans le brouillon pour relecture avant l’envoi.",
    livebridge_preview_title: "Aperçu bilingue actuel",
    livebridge_preview_note:
      "Le mode Live garde un aperçu bilingue prêt et complète l’autre côté après un court délai.",
    livebridge_preview_empty: "La ligne live actuelle apparaîtra ici.",
    livebridge_preview_badge_live: "Live",
    livebridge_preview_badge_ready: "Prêt",
    home_hub_coming_desc: "Something new for stories, voices, and young minds is on the way.",
    home_hub_coming_alias: "",
    livebridge_title: "LiveBridge",
    livebridge_alias: "瞬言",
    livebridge_intro:
      "LiveBridge est un écran compagnon pour les conversations familiales, surtout quand les enfants parlent anglais et que les grands-parents lisent le chinois pendant un appel.",
    livebridge_note:
      "Ce MVP reste volontairement pratique : un Input partagé, un Viewer protégé par mot de passe, la conservation complète du brouillon live, un complément bilingue léger, et un mode local gratuit pour transformer la parole en notes utiles.",
    livebridge_status: "MVP gratuit prêt.",
    livebridge_mode_live: "Live",
    livebridge_mode_organize: "Organize",
    livebridge_mode_intro: "Choisissez le mode avant de commencer.",
    livebridge_mode_live_note:
      "Live garde un brouillon bilingue partagé et l’envoie vers un Viewer privé.",
    livebridge_mode_organize_note:
      "Organize transforme la voix ou le texte collé en notes claires à garder et réutiliser.",
    livebridge_organize_intro:
      "Mode local gratuit : capturez la parole en anglais ou en chinois dans le navigateur et transformez-la en transcript, texte nettoyé, notes structurées, titre, résumé, points clés et actions.",
    livebridge_source_language: "Langue source",
    livebridge_target_language: "Traduire vers",
    livebridge_language_english: "Anglais",
    livebridge_language_chinese: "Chinois",
    livebridge_record_start: "Commencer l’écoute",
    livebridge_record_stop: "Arrêter",
    livebridge_recording_active: "Écoute en cours.",
    livebridge_recording_stopping: "Arrêt de l’écoute…",
    livebridge_organize_button: "Organiser",
    livebridge_live_preview: "Aperçu du transcript en direct",
    livebridge_live_preview_empty: "Votre transcription vocale apparaîtra ici.",
    livebridge_use_transcript_input: "Utiliser la transcription comme entrée",
    livebridge_paste_text: "Coller du texte",
    livebridge_paste_placeholder: "Collez ici des notes vocales ou un transcript brut…",
    livebridge_organize_empty: "Aucun résultat organisé pour l’instant.",
    livebridge_result_placeholder: "Enregistrez votre voix ou collez du texte pour commencer.",
    livebridge_organize_processing: "Organisation du texte…",
    livebridge_organize_ready: "Résultat organisé prêt.",
    livebridge_translation_ready: "Le résultat organisé est prêt, avec une traduction légère ajoutée.",
    livebridge_translation_partial:
      "Le résultat organisé est prêt. La traduction a utilisé le mode bilingue léger.",
    livebridge_translation_failed:
      "Le résultat organisé est prêt. La traduction reste optionnelle dans ce MVP et peut manquer sur cette tentative.",
    livebridge_organize_mock_ready: "Résultat simulé prêt.",
    livebridge_organize_failed: "La requête d’organisation a échoué.",
    livebridge_organize_missing_text: "Ajoutez ou capturez du texte avant d’organiser.",
    livebridge_result_title: "Titre",
    livebridge_result_summary: "Résumé",
    livebridge_action_copy: "Copier",
    livebridge_action_download: "Télécharger TXT",
    livebridge_action_retry: "Relancer",
    livebridge_tab_transcript: "Transcript",
    livebridge_tab_cleaned: "Nettoyé",
    livebridge_tab_structured: "Structuré",
    livebridge_tab_translation: "Traduction",
    livebridge_result_tabs: "Onglets de résultat",
    livebridge_translation_placeholder:
      "Aucune traduction n’a été ajoutée pour cette tentative. Le résultat organisé ci-dessus reste prêt à l’emploi.",
    livebridge_key_points: "Points clés",
    livebridge_action_items: "Actions",
    livebridge_copy_done: "Copié dans le presse-papiers.",
    livebridge_copy_failed: "La copie a échoué.",
    livebridge_download_ready: "Le fichier TXT est prêt."
  },
  zh: {
    echo_tagline: "你的书，随时可读，也可收听。",
    home_reader_lab_note:
      "上传一本书（支持 EPUB、MOBI、PDF、TXT 和 DOCX），按自己的方式浏览它：屏幕阅读、切换音频、逐章跟进，并且随时回到上次停下的位置。",
    home_reader_lab_note_followup:
      "PageTone 支持英语、中文和法语，无需安装应用，也无需创建账号。",
    home_reader_lab_note_close: "打开、播放、继续就好。",
    home_hub_intro: "选择一个板块进入。",
    home_hub_world_desc: "故事、选择、谜题和新世界，都从这里开始。",
    home_hub_world_cta: "进入幻世界",
    home_hub_pagetone_desc: "上传一本书，阅读、收听，并从上次停下的位置继续。",
    home_hub_pagetone_upload_title: "上传一本书",
    home_hub_pagetone_upload_hint: "支持 EPUB、MOBI、PDF、TXT、DOCX 和 DOC",
    home_hub_pagetone_cta: "打开页音",
    home_hub_livebridge_desc: "一个免费优先的中英双语陪伴屏，包含实时捕捉、轻量翻译和本地整理工具。",
    home_hub_livebridge_cta: "进入瞬言",
    livebridge_try_cta: "试用原型",
    livebridge_input_cta: "打开 Input 页面",
    livebridge_viewer_cta: "打开 Viewer 页面",
    livebridge_input_title: "LiveBridge Input",
    livebridge_viewer_title: "LiveBridge Viewer",
    livebridge_tagline: "一个安静、清楚的家庭双语陪伴屏。",
    livebridge_short_desc:
      "LiveBridge 帮助家人保留完整的英文或中文内容，在需要时轻量补出另一侧，并在通话时保持一个私密 viewer 页面。",
    livebridge_how_title: "如何运作",
    livebridge_step_speak: "捕捉完整的英文或中文内容",
    livebridge_step_translate: "补全另一侧或整理文本",
    livebridge_step_view: "在私密页面里持续查看",
    livebridge_usecase_title: "为家庭视频通话而做",
    livebridge_usecase_body:
      "它不是用来替代微信视频，而是作为一个家庭通话陪伴屏，特别适合孩子主要说英语、祖辈主要看中文的沟通场景。它会尽量把完整内容保留下来，并在需要时补上轻量双语支持。",
    livebridge_access_title: "原型入口",
    livebridge_input_intro:
      "说话的一侧使用这个页面。浏览器支持时可以捕捉完整的英文或中文内容，并把全文保留在草稿区，然后直接发送，或先手动调整再发送。如果一侧留空，LiveBridge 会先尝试用轻量方式补出另一侧再显示。",
    livebridge_viewer_intro:
      "Viewer 是给祖辈看的大字字幕页，重点是安静、清楚、容易读，适合在视频通话时并排打开。",
    livebridge_room_label: "共享房间",
    livebridge_room_value: "主舞台",
    livebridge_name_label: "说话人名字",
    livebridge_name_placeholder: "可选名字",
    livebridge_message_label: "手动英文内容",
    livebridge_message_placeholder: "为这个 demo 输入一句英文……",
    livebridge_chinese_label: "手动中文内容",
    livebridge_chinese_placeholder: "为这个 demo 补上一句对应中文……",
    livebridge_send: "发送当前内容",
    livebridge_sending: "发送 / 补全中……",
    livebridge_fill_missing: "补全另一侧",
    livebridge_clear: "清空",
    livebridge_sent: "消息已发送。",
    livebridge_fill_missing_ready: "另一侧内容已经补好了。",
    livebridge_fill_missing_empty: "请先输入英文或中文中的一侧。",
    livebridge_fill_missing_full: "两侧内容都已经有了。先清掉一侧，或直接修改后发送。",
    livebridge_fill_missing_failed: "这次没能补全另一侧，请稍后再试。",
    livebridge_start_listening_en: "识别英文",
    livebridge_start_listening_zh: "识别中文",
    livebridge_stop_listening: "停止监听",
    livebridge_send_test: "发送测试消息",
    livebridge_new_session: "开始新会话",
    livebridge_latest_english: "最新英文",
    livebridge_latest_chinese: "最新中文",
    livebridge_latest_output: "最新输出",
    livebridge_recent_lines: "最近消息",
    livebridge_status_idle: "空闲",
    livebridge_status_listening: "监听中",
    livebridge_status_processing: "处理中",
    livebridge_status_done: "已完成",
    livebridge_status_error: "错误",
    livebridge_status_connected: "已连接",
    livebridge_status_disconnected: "未连接",
    livebridge_status_syncing: "同步中",
    livebridge_status_private: "私密 Viewer",
    livebridge_recording_timer: "录音时间",
    livebridge_listening_english: "当前正在识别英文。",
    livebridge_listening_chinese: "当前正在识别中文。",
    livebridge_speech_note:
      "如果这个浏览器暂时不支持语音捕捉，原型仍然可以通过测试消息以及手动填写英文和中文草稿来运行。捕捉到的全文会保留在草稿区里，发送前也可以先轻量补全另一侧。",
    livebridge_speech_ready: "当前浏览器支持英文或中文语音捕捉。",
    livebridge_speech_unavailable: "当前浏览器暂时不支持语音捕捉。",
    livebridge_session_reset: "新的会话已经开始。",
    livebridge_password_label: "Viewer 密码",
    livebridge_password_placeholder: "输入密码",
    livebridge_unlock: "打开 Viewer",
    livebridge_lock: "锁定 Viewer",
    livebridge_viewer_waiting: "等待消息中……",
    livebridge_viewer_empty: "还没有消息。先去 Input 页面发送一条。",
    livebridge_last_updated: "最近更新",
    livebridge_session_note: "Viewer 权限只保存在当前浏览器会话中。",
    livebridge_draft_note: "草稿会保存在这台设备上，live 捕捉到的全文现在也会保留在草稿区里，方便发送前检查。",
    livebridge_preview_title: "当前双语预览",
    livebridge_preview_note: "实时模式会保持一份双语预览，并在短延时后尽量补齐另一侧。",
    livebridge_preview_empty: "当前这句 live 内容会显示在这里。",
    livebridge_preview_badge_live: "实时中",
    livebridge_preview_badge_ready: "已就绪",
    home_hub_coming_desc: "Something new for stories, voices, and young minds is on the way.",
    home_hub_coming_alias: "",
    livebridge_title: "LiveBridge",
    livebridge_alias: "瞬言",
    livebridge_intro:
      "LiveBridge 是一个给家庭通话使用的双语陪伴页，尤其适合孩子讲英语、祖辈读中文的场景。",
    livebridge_note:
      "这个 MVP 刻意保持实用：一个共享 Input，一个带密码的 Viewer，live 全文保留，轻量双语补全，以及一个免费本地整理模式，用来把语音变成更可用的文字。",
    livebridge_status: "免费优先原型已准备好。",
    livebridge_mode_live: "实时",
    livebridge_mode_organize: "整理",
    livebridge_mode_intro: "开始前先选模式。",
    livebridge_mode_live_note: "实时模式会保留共享双语草稿，并发送到私密 Viewer。",
    livebridge_mode_organize_note: "整理模式会把语音或粘贴文本变成清晰、可复用的笔记。",
    livebridge_organize_intro:
      "免费本地模式：先用浏览器捕捉英文或中文语音，再把内容整理成转写、清理文本、结构化笔记、标题、摘要、关键点和行动项。",
    livebridge_source_language: "源语言",
    livebridge_target_language: "翻译为",
    livebridge_language_english: "英文",
    livebridge_language_chinese: "中文",
    livebridge_record_start: "开始识别",
    livebridge_record_stop: "停止",
    livebridge_recording_active: "正在识别语音。",
    livebridge_recording_stopping: "正在停止识别……",
    livebridge_organize_button: "整理内容",
    livebridge_live_preview: "实时转写预览",
    livebridge_live_preview_empty: "语音转写会显示在这里。",
    livebridge_use_transcript_input: "使用转写作为输入",
    livebridge_paste_text: "粘贴文本",
    livebridge_paste_placeholder: "把语音记录或原始转写内容粘贴到这里……",
    livebridge_organize_empty: "还没有整理结果。",
    livebridge_result_placeholder: "先录音或粘贴文本，再开始整理。",
    livebridge_organize_processing: "正在整理文本……",
    livebridge_organize_ready: "整理结果已生成。",
    livebridge_translation_ready: "整理结果已完成，并补上了轻量翻译。",
    livebridge_translation_partial: "整理结果已完成，这次翻译使用了轻量双语模式。",
    livebridge_translation_failed: "整理结果已完成。翻译在这个 MVP 里是附加能力，这次可能暂时没有生成。",
    livebridge_organize_mock_ready: "模拟整理结果已生成。",
    livebridge_organize_failed: "整理请求失败。",
    livebridge_organize_missing_text: "请先输入或识别一些文本，再进行整理。",
    livebridge_result_title: "标题",
    livebridge_result_summary: "摘要",
    livebridge_action_copy: "复制",
    livebridge_action_download: "下载 TXT",
    livebridge_action_retry: "重试",
    livebridge_tab_transcript: "原始转写",
    livebridge_tab_cleaned: "清理文本",
    livebridge_tab_structured: "结构化",
    livebridge_tab_translation: "翻译",
    livebridge_result_tabs: "结果标签",
    livebridge_translation_placeholder: "这次没有补上翻译，但上面的整理结果已经可以直接使用。",
    livebridge_key_points: "关键点",
    livebridge_action_items: "行动项",
    livebridge_copy_done: "已复制到剪贴板。",
    livebridge_copy_failed: "复制失败。",
    livebridge_download_ready: "TXT 文件已准备好。"
  }
};

const ABOUT_CONTENT = {
  en: {
    paragraphs: [
      "Wonder World began with something very personal. As a father, I've always wanted to better understand my child's inner world — his thoughts, feelings, and the way he sees things. But as children grow older, many families notice the same quiet gap: everyone cares, yet real conversation doesn't always come easily. For me, language differences made that gap feel even wider. So I kept asking myself: is there a more natural, lower-pressure way for parents to truly understand their children?",
      "This game grew out of that question. I started by letting my child engage with AI through story-driven, imaginative activities — adventures, choices, puzzles, and challenges. I noticed that in these playful settings, children often reveal their interests, thinking patterns, and emotional cues far more naturally than in everyday conversation. That observation led me somewhere bigger: could this become something that helps other families too?",
      "Wonder World is, first and foremost, a game children actually want to play. It's not a workbook in disguise, and children shouldn't feel like they're being studied. They explore stories, make choices, solve puzzles, earn stars, and unlock new worlds. Along the way, parents may receive a few gentle observations — whether a child leans toward exploration or planning, how they respond to difficulty, what themes draw them in or make them pause. These are not labels or conclusions, and this is not a psychological test. I hope it can simply be a starting point for better conversations.",
      "This project is still evolving. Many people using this version already know me personally — so in some ways, this is also an open experiment: turning a real challenge I've faced as a father into something that might be useful to others.",
      "I'll keep working to make Wonder World more fun, more thoughtful, and more trustworthy. Your honest feedback is genuinely welcome."
    ],
    privacy:
      "A note on privacy: I do not and cannot collect any information about your child. All game files and analysis are processed entirely on your local device. Please feel free to use this with peace of mind.",
    gentle:
      "The observations in this game are meant only to offer parents light reflections on how children respond to stories and challenges. They are not a diagnosis and should not be treated as fixed conclusions."
  },
  fr: {
    paragraphs: [
      "Wonder World est né d'une motivation très personnelle. En tant que père, j'ai toujours voulu mieux comprendre le monde intérieur de mon enfant — ses pensées, ses émotions et sa manière de voir les choses. Mais à mesure que les enfants grandissent, beaucoup de familles ressentent le même décalage discret : tout le monde se soucie les uns des autres, mais les vraies conversations ne viennent pas toujours naturellement. Dans mon cas, les différences de langue ont encore accentué ce sentiment. Alors je me suis souvent posé la question : existe-t-il une manière plus naturelle et moins pressante pour aider les parents à vraiment comprendre leurs enfants ?",
      "Ce jeu est né de cette réflexion. J'ai commencé par laisser mon enfant interagir avec l'IA à travers des activités narratives et imaginatives — aventures, choix, énigmes et défis. J'ai constaté que, dans ce cadre ludique, les enfants révèlent souvent leurs centres d'intérêt, leurs façons de raisonner et certains signaux émotionnels beaucoup plus spontanément que dans les conversations du quotidien. Cette observation m'a conduit plus loin : cela pourrait-il aussi aider d'autres familles ?",
      "Wonder World est avant tout un jeu que les enfants ont réellement envie de jouer. Ce n'est pas un cahier d'exercices déguisé, et les enfants ne doivent jamais avoir l'impression d'être analysés. Ils explorent des histoires, font des choix, résolvent des énigmes, gagnent des étoiles et débloquent de nouveaux mondes. En parallèle, les parents peuvent recevoir quelques observations bienveillantes — par exemple si l'enfant penche plutôt vers l'exploration ou la planification, comment il réagit face à la difficulté, ou quels thèmes l'attirent ou le font hésiter. Ce ne sont ni des étiquettes, ni des conclusions, et ce n'est pas un test psychologique. J'espère simplement que cela pourra servir de point de départ à de meilleures conversations.",
      "Le projet continue d'évoluer. Beaucoup de personnes qui utilisent cette version me connaissent déjà personnellement ; d'une certaine manière, c'est aussi une expérience ouverte : transformer un défi réel que j'ai vécu en tant que père en quelque chose qui pourrait être utile à d'autres.",
      "Je continuerai à améliorer Wonder World pour le rendre plus amusant, plus réfléchi et plus fiable. Vos retours sincères sont les bienvenus."
    ],
    privacy:
      "Note sur la confidentialité : je ne collecte pas et ne peux pas collecter d'informations sur votre enfant. Tous les fichiers du jeu et les analyses sont traités uniquement sur votre appareil local. Vous pouvez l'utiliser en toute tranquillité.",
    gentle:
      "Les observations proposées dans ce jeu visent uniquement à offrir aux parents des pistes de réflexion légères sur la manière dont les enfants réagissent aux histoires et aux défis. Elles ne constituent pas un diagnostic et ne doivent pas être considérées comme des conclusions définitives."
  }
};

const CURATED_FR_TEXT = {
  // Story worlds
  "Your spaceship lands on a mysterious alien planet. Your ship's engine is out of energy. You must explore the planet and find enough energy to restart it.":
    "Ton vaisseau spatial atterrit sur une mystérieuse planète extraterrestre. Le moteur n'a plus d'énergie. Tu dois explorer la planète et trouver assez d'énergie pour le redémarrer.",
  "Explore a glowing cave nearby": "Explorer une grotte lumineuse voisine",
  "Walk toward a strange alien forest": "Avancer vers une étrange forêt extraterrestre",
  "Inside the cave you see glowing crystals floating in the air. Your scanner says they contain energy.":
    "Dans la grotte, tu vois des cristaux lumineux flotter dans l'air. Ton scanner indique qu'ils contiennent de l'énergie.",
  "Collect some crystals": "Ramasser quelques cristaux",
  "Study strange symbols on the cave wall": "Étudier les symboles étranges sur la paroi",
  "The alien forest is full of strange moving plants. Some plants glow softly.":
    "La forêt extraterrestre est remplie de plantes étranges en mouvement. Certaines brillent doucement.",
  "Collect glowing plants for research": "Prélever des plantes lumineuses pour la recherche",
  "Follow a trail of moving plants": "Suivre une piste de plantes en mouvement",
  "The symbols seem to describe how aliens stored energy long ago.":
    "Ces symboles semblent expliquer comment les extraterrestres stockaient l'énergie autrefois.",
  "Try to decode the symbols": "Essayer de déchiffrer les symboles",
  "Follow the map shown in the symbols": "Suivre la carte montrée par les symboles",
  "You discover ancient alien ruins with a strange energy device in the center.":
    "Tu découvres d'anciennes ruines extraterrestres avec un étrange dispositif énergétique au centre.",
  "Examine the device carefully": "Examiner l'appareil avec attention",
  "Search the ruins for more clues": "Chercher d'autres indices dans les ruines",
  "Behind the ruins you find an ancient alien energy reactor. It might power your spaceship.":
    "Derrière les ruines, tu trouves un ancien réacteur extraterrestre. Il pourrait alimenter ton vaisseau.",
  "Try to activate the reactor": "Tenter d'activer le réacteur",
  "You arrive at a vast wildlife reserve filled with amazing animals. Your mission is to observe the animals and help protect the ecosystem.":
    "Tu arrives dans une vaste réserve naturelle remplie d'animaux fascinants. Ta mission est de les observer et de protéger l'écosystème.",
  "Follow fresh animal footprints": "Suivre des empreintes d'animaux fraîches",
  "Climb a hill to observe animals from above": "Monter sur une colline pour observer les animaux d'en haut",
  "You follow the footprints and discover several groups of deer near a river.":
    "Tu suis les empreintes et découvres plusieurs groupes de cerfs près d'une rivière.",
  "Count the deer carefully": "Compter les cerfs avec attention",
  "Continue following the tracks deeper into the forest":
    "Continuer à suivre les traces plus profondément dans la forêt",
  "From the hill you see many animals moving across the grassland.":
    "Depuis la colline, tu vois de nombreux animaux se déplacer dans la prairie.",
  "Use binoculars to count them": "Utiliser des jumelles pour les compter",
  "Walk toward a herd of animals": "Avancer vers un troupeau d'animaux",
  "Inside the forest you discover a group of monkeys jumping between trees.":
    "Dans la forêt, tu découvres un groupe de singes qui saute d'arbre en arbre.",
  "Observe how they move in groups": "Observer comment ils se déplacent en groupe",
  "Search the forest for other animals": "Chercher d'autres animaux dans la forêt",
  "You reach a peaceful lake where animals gather to drink water.":
    "Tu arrives à un lac paisible où les animaux se rassemblent pour boire.",
  "Observe the animals at the lake": "Observer les animaux au bord du lac",
  "You arrive at a busy farm where animals and crops need care. Your mission is to help the farmer manage the farm.":
    "Tu arrives dans une ferme très active où les animaux et les cultures ont besoin de soins. Ta mission est d'aider à gérer la ferme.",
  "Feed the animals": "Nourrir les animaux",
  "Check the apple orchard": "Vérifier le verger de pommiers",
  "The cows and sheep are waiting for food.":
    "Les vaches et les moutons attendent leur nourriture.",
  "Prepare food for the animals": "Préparer la nourriture des animaux",
  "Count the animals in the field": "Compter les animaux dans le champ",
  "The apple trees are full of fruit ready to harvest.":
    "Les pommiers sont chargés de fruits prêts à être récoltés.",
  "Collect apples": "Ramasser des pommes",
  "Inspect the orchard rows": "Inspecter les rangées du verger",
  "Inside the barn you find tools and baskets for the harvest.":
    "Dans la grange, tu trouves des outils et des paniers pour la récolte.",
  "Organize the supplies": "Organiser les provisions",
  "Prepare food storage": "Préparer l'espace de stockage",
  "The farm is ready for the final harvest before sunset.":
    "La ferme est prête pour la récolte finale avant le coucher du soleil.",
  "Finish today's farm work": "Terminer le travail de la ferme pour aujourd'hui",
  "You arrive in a futuristic robot city where machines build everything automatically.":
    "Tu arrives dans une ville robotique futuriste où les machines fabriquent tout automatiquement.",
  "Visit the robot factory": "Visiter l'usine de robots",
  "Explore the hover train station": "Explorer la station de train à sustentation",
  "Inside the factory robots assemble new machines every minute.":
    "Dans l'usine, des robots assemblent de nouvelles machines chaque minute.",
  "Observe robot production": "Observer la production des robots",
  "Inspect the assembly lines": "Inspecter les lignes d'assemblage",
  "Hover trains transport robots across the city.":
    "Des trains à sustentation transportent des robots à travers toute la ville.",
  "Count the trains arriving": "Compter les trains qui arrivent",
  "Follow a robot delivery route": "Suivre un itinéraire de livraison robotisée",
  "You discover the city control center where robots are managed.":
    "Tu découvres le centre de contrôle de la ville, où les robots sont pilotés.",
  "Analyze the robot network": "Analyser le réseau de robots",
  "Check the energy supply system": "Vérifier le système d'alimentation en énergie",
  "At the center of the city lies the AI energy core powering everything.":
    "Au cœur de la ville se trouve le noyau énergétique de l'IA qui alimente tout.",
  "Stabilize the energy core": "Stabiliser le noyau énergétique",
  "You arrive at a mysterious magic academy where young wizards learn powerful spells.":
    "Tu arrives dans une mystérieuse académie de magie où de jeunes sorciers apprennent des sorts puissants.",
  "Visit the spell library": "Visiter la bibliothèque des sorts",
  "Walk to the potion laboratory": "Aller au laboratoire de potions",
  "The library is filled with glowing spell books.":
    "La bibliothèque est remplie de grimoires lumineux.",
  "Study a spell book": "Étudier un grimoire",
  "Search ancient scrolls": "Chercher des parchemins anciens",
  "The potion lab bubbles with magical liquids.":
    "Le laboratoire de potions bouillonne de liquides magiques.",
  "Mix potion ingredients": "Mélanger des ingrédients de potion",
  "Examine potion shelves": "Examiner les étagères de potions",
  "At the top of the wizard tower you find magical crystals used to power spells.":
    "Au sommet de la tour des sorciers, tu trouves des cristaux magiques qui alimentent les sorts.",
  "Study the crystal energy": "Étudier l'énergie des cristaux",
  "Follow the magic energy flow": "Suivre le flux d'énergie magique",
  "You discover a magical portal that requires energy to open.":
    "Tu découvres un portail magique qui a besoin d'énergie pour s'ouvrir.",
  "Activate the portal": "Activer le portail",
  "A strange virus has turned many people into zombies. Your mission is to help survivors escape the city.":
    "Un étrange virus a transformé de nombreuses personnes en zombies. Ta mission est d'aider les survivants à s'échapper de la ville.",
  "Search an abandoned house": "Fouiller une maison abandonnée",
  "Explore a quiet street": "Explorer une rue silencieuse",
  "Inside the house you find food supplies and survival gear.":
    "Dans la maison, tu trouves des réserves de nourriture et du matériel de survie.",
  "Collect food supplies": "Récupérer des réserves de nourriture",
  "Search the basement": "Fouiller le sous-sol",
  "The street is quiet, but you hear zombies in the distance.":
    "La rue est calme, mais tu entends des zombies au loin.",
  "Count zombie groups carefully": "Compter les groupes de zombies avec attention",
  "Run toward the school building": "Courir vers le bâtiment de l'école",
  "The school has become a safe shelter for survivors.":
    "L'école est devenue un abri sûr pour les survivants.",
  "Organize the survivors": "Organiser les survivants",
  "Search the building for resources": "Chercher des ressources dans le bâtiment",
  "You find a bus that can take everyone out of the city.":
    "Tu trouves un bus qui peut évacuer tout le monde hors de la ville.",
  "Prepare the final escape plan": "Préparer le plan d'évacuation final",

  // Boss questions
  "The reactor control panel unlocks only if you solve the equation correctly.":
    "Le panneau de contrôle du réacteur se déverrouille seulement si tu résous correctement l'équation.",
  "Solve 3(2x - 5) = 39. What is x?": "Résous 3(2x - 5) = 39. Quelle est la valeur de x ?",
  "Divide by 3: 2x - 5 = 13. Then 2x = 18, so x = 9.":
    "Divise par 3 : 2x - 5 = 13. Puis 2x = 18, donc x = 9.",
  "You must estimate real distance to guide rescue teams across the reserve.":
    "Tu dois estimer la distance réelle pour guider les équipes de secours à travers la réserve.",
  "Map scale is 1:25,000. Two points are 7.2 cm apart on the map. What is the real distance?":
    "L'échelle de la carte est 1:25 000. Deux points sont séparés de 7,2 cm sur la carte. Quelle est la distance réelle ?",
  "7.2 × 25,000 = 180,000 cm = 1,800 m = 1.8 km.":
    "7,2 × 25 000 = 180 000 cm = 1 800 m = 1,8 km.",
  "The final harvest plan requires exact field area calculations.":
    "Le plan final de récolte exige un calcul précis des surfaces.",
  "A field is 48 m by 35 m. If 3/5 of it is planted with corn, what area is corn?":
    "Un champ mesure 48 m sur 35 m. Si 3/5 de la surface est plantée en maïs, quelle est la surface plantée en maïs ?",
  "Total area = 48 × 35 = 1680 m². Corn area = 1680 × 3/5 = 1008 m².":
    "Surface totale = 48 × 35 = 1 680 m². Surface de maïs = 1 680 × 3/5 = 1 008 m².",
  "The city core loses energy by a fixed percentage each mission cycle.":
    "Le noyau énergétique de la ville perd un pourcentage fixe à chaque cycle de mission.",
  "A battery starts at 100%. It loses 12% of full capacity each mission. After 4 missions, how much remains?":
    "Une batterie commence à 100 %. Elle perd 12 % de sa capacité totale à chaque mission. Après 4 missions, combien reste-t-il ?",
  "Loss is 4 × 12% = 48%, so remaining energy is 100% - 48% = 52%.":
    "La perte est de 4 × 12 % = 48 %, donc l'énergie restante est 100 % - 48 % = 52 %.",
  "The portal opens only when two rune groups are activated together.":
    "Le portail s'ouvre uniquement lorsque deux groupes de runes sont activés ensemble.",
  "2/3 of 45 runes and 40% of 30 runes must glow. How many runes glow in total?":
    "Les 2/3 de 45 runes et 40 % de 30 runes doivent briller. Combien de runes brillent au total ?",
  "2/3 of 45 = 30, and 40% of 30 = 12. Total = 30 + 12 = 42.":
    "Les 2/3 de 45 = 30, et 40 % de 30 = 12. Total = 30 + 12 = 42.",
  "Before departure, you must calculate transport capacity correctly.":
    "Avant le départ, tu dois calculer correctement la capacité de transport.",
  "Each bus carries 36 people. There are 5 survivor groups of 8 people each. Minimum number of buses needed?":
    "Chaque bus transporte 36 personnes. Il y a 5 groupes de survivants de 8 personnes chacun. Quel est le nombre minimal de bus nécessaires ?",
  "Total people = 5 × 8 = 40. One bus is not enough, so you need 2 buses.":
    "Nombre total de personnes = 5 × 8 = 40. Un seul bus ne suffit pas, il en faut donc 2.",
  "Your purifier removes 90% of bacteria in one cycle.":
    "Ton purificateur élimine 90 % des bactéries en un cycle.",
  "After purification, 1,000 bacteria remain. About how many bacteria were there before the cycle?":
    "Après purification, il reste 1 000 bactéries. Environ combien y en avait-il avant le cycle ?",
  "If 90% is removed, 10% remains. 1,000 is 10%, so original amount is 10,000.":
    "Si 90 % sont éliminées, 10 % restent. 1 000 représente 10 %, donc la quantité initiale était de 10 000.",

  // Common option/term translations used by questions
  Red: "Rouge",
  Blue: "Bleu",
  Green: "Vert",
  White: "Blanc",
  Yellow: "Jaune",
  "All of them": "Toutes les portes",
  Helmet: "Casque",
  Boots: "Bottes",
  Backpack: "Sac à dos",
  Gloves: "Gants",
  "4 crystals": "4 cristaux",
  "3 crystals": "3 cristaux",
  "4 boxes": "4 boîtes",
  "3 boxes": "3 boîtes",
  "They are the same": "Elles sont identiques",
  "Not enough information": "Informations insuffisantes",
  "They may react to darkness": "Elles peuvent réagir à l'obscurité",
  "They are made of metal": "Elles sont faites de métal",
  "They are animals": "Ce sont des animaux",
  "They can fly": "Elles peuvent voler",
  "A lake": "Un lac",
  "A cave": "Une grotte",
  "A mountain": "Une montagne",
  "A robot": "Un robot",
  Scanner: "Scanner",
  "Food pack": "Paquet de nourriture",
  Rope: "Corde",
  Blanket: "Couverture",
  Snake: "Serpent",
  Frog: "Grenouille",
  Whale: "Baleine",
  Lizard: "Lézard",
  "To see far-away animals clearly": "Pour voir clairement les animaux éloignés",
  "To make sounds louder": "Pour amplifier les sons",
  "To smell plants": "Pour sentir les plantes",
  "To keep warm": "Pour rester au chaud",
  Sheep: "Moutons",
  Cows: "Vaches",
  Sunlight: "Lumière du soleil",
  Plastic: "Plastique",
  Sandwiches: "Sandwichs",
  Shoes: "Chaussures",
  "Red barn": "Grange rouge",
  "Blue barn": "Grange bleue",
  "Both barns": "Les deux granges",
  "Neither barn": "Aucune des deux",
  "Robot A": "Robot A",
  "Robot B": "Robot B",
  Both: "Les deux",
  Neither: "Aucun des deux",
  "To make power last longer": "Pour faire durer l'énergie plus longtemps",
  "To make roads smaller": "Pour rendre les routes plus petites",
  "To make robots heavier": "Pour rendre les robots plus lourds",
  "To stop math": "Pour arrêter les maths",
  "Silver key": "Clé en argent",
  "Gold key": "Clé en or",
  "Blue key": "Clé bleue",
  "No key": "Aucune clé",
  "Heat changed the potion": "La chaleur a modifié la potion",
  "The table changed it": "La table l'a modifiée",
  "The book changed it": "Le livre l'a modifiée",
  "Nothing changed it": "Rien ne l'a modifiée",
  "The portal cannot open yet": "Le portail ne peut pas encore s'ouvrir",
  "The portal is already open": "Le portail est déjà ouvert",
  "The portal will open by itself": "Le portail s'ouvrira tout seul",
  "The portal is broken forever": "Le portail est cassé pour toujours",
  "The gate cannot open yet": "La porte ne peut pas encore s'ouvrir",
  "The gate is already open": "La porte est déjà ouverte",
  "The code does not matter ever": "Le code n'a alors aucune importance",
  "The zombies opened the gate": "Les zombies ont ouvert la porte",
  "Spell book": "Livre de sorts",
  Pillow: "Oreiller",
  "Empty cup": "Tasse vide",
  Shoe: "Chaussure",
  Food: "Nourriture",
  Toy: "Jouet",
  Rock: "Pierre",
  Poster: "Affiche",
  "Quiet open road": "Route dégagée et calme",
  "Blocked road": "Route bloquée",
  "Noisy road": "Route bruyante",
  "Broken road": "Route endommagée",

  // Question contexts, stems, and explanations
  "Your spaceship collects small energy crystals.":
    "Ton vaisseau collecte de petits cristaux d'énergie.",
  "You collect 3 crystals. Each has 2 energy points. Total energy?":
    "Tu ramasses 3 cristaux. Chaque cristal fournit 2 points d'énergie. Énergie totale ?",
  "3 × 2 = 6.": "3 × 2 = 6.",
  "You find three doors inside the alien cave.": "Tu trouves trois portes dans la grotte extraterrestre.",
  "The clue says: 'The safe door is not red and not green.' Which door is safe?":
    "L'indice dit : « La porte sûre n'est ni rouge ni verte. » Quelle porte est sûre ?",
  "If it is not red and not green, it must be blue.":
    "Si elle n'est ni rouge ni verte, alors elle est forcément bleue.",
  "Your scanner studies the planet sky.": "Ton scanner analyse le ciel de la planète.",
  "Which of these helps astronauts breathe in space?":
    "Parmi ces objets, lequel aide les astronautes à respirer dans l'espace ?",
  "The helmet is part of the life-support system.":
    "Le casque fait partie du système de survie.",
  "Alien lights blink in a pattern near the ruins.":
    "Près des ruines, des lumières extraterrestres clignotent selon un motif.",
  "What comes next? 2, 4, 8, 16, ?": "Quel nombre vient ensuite ? 2, 4, 8, 16, ?",
  "The number doubles each time.": "Le nombre double à chaque étape.",
  "You can choose one energy source for your scanner.":
    "Tu peux choisir une seule source d'énergie pour ton scanner.",
  "Which gives more total energy: 4 crystals worth 5 each, or 3 crystals worth 7 each?":
    "Quelle option donne le plus d'énergie au total : 4 cristaux de 5 points chacun, ou 3 cristaux de 7 points ?",
  "4×5=20, while 3×7=21. So 3 crystals gives more energy.":
    "4×5=20, tandis que 3×7=21. Donc 3 cristaux donnent plus d'énergie.",
  "Alien plants glow only at night.": "Les plantes extraterrestres brillent seulement la nuit.",
  "What does that suggest?": "Que peut-on en déduire ?",
  "Glowing only at night suggests they respond to darkness.":
    "Le fait qu'elles brillent seulement la nuit suggère qu'elles réagissent à l'obscurité.",
  "Your engine needs power cells to restart.":
    "Ton moteur a besoin de cellules d'énergie pour redémarrer.",
  "You have 5 cells worth 8 energy each. Total energy?":
    "Tu as 5 cellules valant 8 points d'énergie chacune. Énergie totale ?",
  "5 × 8 = 40.": "5 × 8 = 40.",
  "The reactor has four switches. Only one opens the gate.":
    "Le réacteur a quatre interrupteurs. Un seul ouvre la porte.",
  "A clue says: 'Switch A does not work. Switch D works only if A works.' Which switch is impossible?":
    "Un indice dit : « L'interrupteur A ne fonctionne pas. L'interrupteur D fonctionne seulement si A fonctionne. » Quel interrupteur est impossible ?",
  "If A does not work, then D cannot work either.":
    "Si A ne fonctionne pas, alors D ne peut pas fonctionner non plus.",
  "You can carry only one tool into the ruins.":
    "Tu ne peux emporter qu'un seul outil dans les ruines.",
  "Which tool is the best choice if you want to study symbols on the wall?":
    "Quel outil est le meilleur choix si tu veux étudier les symboles sur le mur ?",
  "A scanner is the most useful tool for studying symbols.":
    "Un scanner est l'outil le plus utile pour étudier des symboles.",
  "You see several deer near the river.": "Tu vois plusieurs cerfs près de la rivière.",
  "There are 4 groups of deer. Each group has 3 deer. How many deer are there?":
    "Il y a 4 groupes de cerfs. Chaque groupe compte 3 cerfs. Combien y a-t-il de cerfs ?",
  "4 × 3 = 12 deer.": "4 × 3 = 12 cerfs.",
  "You observe animals near the lake.": "Tu observes des animaux près du lac.",
  "Which animal is a mammal?": "Quel animal est un mammifère ?",
  "Whales are mammals.": "Les baleines sont des mammifères.",
  "You use binoculars from the hill.": "Tu utilises des jumelles depuis la colline.",
  "What is the best reason to use binoculars?":
    "Quelle est la meilleure raison d'utiliser des jumelles ?",
  "Binoculars help you see things far away.":
    "Les jumelles aident à voir clairement ce qui est loin.",
  "You observe monkeys jumping between trees.":
    "Tu observes des singes qui sautent entre les arbres.",
  "Each tree has 5 monkeys. There are 4 trees. How many monkeys?":
    "Chaque arbre compte 5 singes. Il y a 4 arbres. Combien de singes ?",
  "4 × 5 = 20 monkeys.": "4 × 5 = 20 singes.",
  "Animal movement follows a pattern.": "Les déplacements des animaux suivent un motif.",
  "What comes next? 3, 6, 12, 24, ?": "Quel nombre vient ensuite ? 3, 6, 12, 24, ?",
  "You want to protect the animals without disturbing them.":
    "Tu veux protéger les animaux sans les déranger.",
  "Which is the best choice?": "Quel est le meilleur choix ?",
  "Watch quietly from far away": "Observer calmement de loin",
  "Run toward them quickly": "Courir rapidement vers eux",
  "Throw food near them": "Lancer de la nourriture près d'eux",
  "Shout to get attention": "Crier pour attirer l'attention",
  "That lets you observe without disturbing them.":
    "Cela te permet d'observer sans les perturber.",
  "Animals gather near the lake in groups.":
    "Les animaux se rassemblent près du lac en groupes.",
  "Each group has 7 animals. There are 6 groups. How many animals?":
    "Chaque groupe compte 7 animaux. Il y a 6 groupes. Combien d'animaux ?",
  "6 × 7 = 42 animals.": "6 × 7 = 42 animaux.",
  "You compare two herds from different parts of the reserve.":
    "Tu compares deux troupeaux venant de zones différentes de la réserve.",
  "Which is greater: 6 groups of 5 animals, or 4 groups of 7 animals?":
    "Quelle quantité est la plus grande : 6 groupes de 5 animaux, ou 4 groupes de 7 animaux ?",
  "6 groups of 5": "6 groupes de 5",
  "4 groups of 7": "4 groupes de 7",
  "They are equal": "Ils sont égaux",
  "6×5=30 while 4×7=28.": "6×5=30 alors que 4×7=28.",
  "You are following tracks through the forest.":
    "Tu suis des traces à travers la forêt.",
  "If fresh tracks always lead to water, and these tracks are fresh, what is most likely nearby?":
    "Si des traces fraîches mènent toujours à l'eau, et que ces traces sont fraîches, qu'y a-t-il probablement à proximité ?",
  "If fresh tracks always lead to water, water is the best answer.":
    "Si des traces fraîches mènent toujours à l'eau, la meilleure réponse est l'eau.",
  "You feed the cows in the field.": "Tu nourris les vaches dans le champ.",
  "There are 4 cows. Each cow eats 3 apples. How many apples are needed?":
    "Il y a 4 vaches. Chaque vache mange 3 pommes. Combien de pommes faut-il ?",
  "4 × 3 = 12 apples.": "4 × 3 = 12 pommes.",
  "You collect eggs from chickens.": "Tu ramasses des œufs au poulailler.",
  "Each chicken lays 2 eggs. You have 5 chickens. How many eggs?":
    "Chaque poule pond 2 œufs. Tu as 5 poules. Combien d'œufs ?",
  "5 × 2 = 10 eggs.": "5 × 2 = 10 œufs.",
  "The animals are hungry and waiting.": "Les animaux ont faim et attendent.",
  "What should you do first?": "Que dois-tu faire en premier ?",
  "Feed the hungry animals": "Nourrir les animaux affamés",
  "Take a nap": "Faire une sieste",
  "Hide the food": "Cacher la nourriture",
  "Leave the farm": "Quitter la ferme",
  "Feeding the animals is the most responsible first step.":
    "Nourrir les animaux est la première action la plus responsable.",
  "You harvest apples from the orchard.": "Tu récoltes des pommes dans le verger.",
  "Each tree produces 6 apples. There are 5 trees. How many apples?":
    "Chaque arbre produit 6 pommes. Il y a 5 arbres. Combien de pommes ?",
  "5 × 6 = 30 apples.": "5 × 6 = 30 pommes.",
  "You compare two farm deliveries.": "Tu compares deux livraisons à la ferme.",
  "Which is more: 7 sheep eating 4 carrots each, or 6 cows eating 5 apples each?":
    "Quelle quantité est la plus grande : 7 moutons mangeant 4 carottes chacun, ou 6 vaches mangeant 5 pommes chacune ?",
  "7×4=28 while 6×5=30.": "7×4=28 alors que 6×5=30.",
  "You are helping crops grow.": "Tu aides les cultures à pousser.",
  "Which of these helps plants grow?":
    "Parmi ces éléments, lequel aide les plantes à pousser ?",
  "Plants need sunlight to grow.": "Les plantes ont besoin de soleil pour pousser.",
  "You store harvested crops in baskets.":
    "Tu ranges les récoltes dans des paniers.",
  "Each basket holds 8 apples. You fill 6 baskets. Total apples?":
    "Chaque panier contient 8 pommes. Tu remplis 6 paniers. Total de pommes ?",
  "6 × 8 = 48 apples.": "6 × 8 = 48 pommes.",
  "The farmer says the red barn stores tools, not food. The blue barn stores food. You need food.":
    "La personne de la ferme dit que la grange rouge contient les outils, pas la nourriture. La grange bleue contient la nourriture. Tu as besoin de nourriture.",
  "Which barn should you go to?": "Vers quelle grange dois-tu aller ?",
  "The clue says the blue barn stores food.":
    "L'indice dit que la grange bleue contient la nourriture.",
  "You can only do one thing before sunset.":
    "Tu ne peux faire qu'une seule chose avant le coucher du soleil.",
  "Which task should come first if the animals are still hungry?":
    "Quelle tâche doit passer en premier si les animaux ont encore faim ?",
  "Paint the fence": "Peindre la clôture",
  "Sort old boxes": "Trier de vieilles boîtes",
  "Count clouds": "Compter les nuages",
  "The hungry animals need urgent care first.":
    "Les animaux affamés ont besoin de soins urgents en priorité.",
  "Robots are produced in the factory.":
    "Des robots sont produits dans l'usine.",
  "Each machine builds 3 robots. There are 4 machines. How many robots?":
    "Chaque machine fabrique 3 robots. Il y a 4 machines. Combien de robots ?",
  "4 × 3 = 12 robots.": "4 × 3 = 12 robots.",
  "Hover taxis wait at stations.": "Des taxis volants attendent dans les stations.",
  "Each station has 5 taxis. There are 2 stations. Total taxis?":
    "Chaque station a 5 taxis. Il y a 2 stations. Total de taxis ?",
  "5 × 2 = 10 taxis.": "5 × 2 = 10 taxis.",
  "A robot scientist gives you a clue.": "Un robot scientifique te donne un indice.",
  "If Robot A is charging and Robot B is not, which robot has power right now?":
    "Si le robot A est en charge et le robot B ne l'est pas, quel robot a de l'énergie maintenant ?",
  "Charging means Robot A has power.":
    "Être en charge signifie que le robot A a de l'énergie.",
  "Delivery robots transport packages.": "Des robots de livraison transportent des colis.",
  "Each robot carries 4 boxes. There are 6 robots. Total boxes?":
    "Chaque robot transporte 4 boîtes. Il y a 6 robots. Total de boîtes ?",
  "6 × 4 = 24 boxes.": "6 × 4 = 24 boîtes.",
  "The city runs on energy cells.":
    "La ville fonctionne grâce à des cellules d'énergie.",
  "What is the best reason to save energy in a city?":
    "Quelle est la meilleure raison d'économiser l'énergie dans une ville ?",
  "Saving energy helps resources last longer.":
    "Économiser l'énergie permet aux ressources de durer plus longtemps.",
  "The city lights blink in a repeating pattern.":
    "Les lumières de la ville clignotent selon un motif répétitif.",
  "What comes next? Red, Blue, Red, Blue, ?":
    "Quel élément vient ensuite ? Rouge, Bleu, Rouge, Bleu, ?",
  "The pattern repeats red, blue.": "Le motif se répète : rouge, bleu.",
  "The robot factory produces drones.": "L'usine robotique produit des drones.",
  "Each line builds 8 drones per hour. There are 5 lines. Total drones?":
    "Chaque ligne produit 8 drones par heure. Il y a 5 lignes. Total de drones ?",
  "5 × 8 = 40 drones.": "5 × 8 = 40 drones.",
  "The city control core has four switches.":
    "Le noyau de contrôle de la ville a quatre interrupteurs.",
  "A clue says: 'Switch A is broken. Switch D works only if A works.' Which switch is impossible?":
    "Un indice dit : « L'interrupteur A est en panne. L'interrupteur D fonctionne seulement si A fonctionne. » Quel interrupteur est impossible ?",
  "If A is broken, D cannot work.": "Si A est en panne, D ne peut pas fonctionner.",
  "You can only bring one tool to fix a robot control panel.":
    "Tu ne peux apporter qu'un seul outil pour réparer un panneau de contrôle robotique.",
  "Which is the most useful tool?": "Quel est l'outil le plus utile ?",
  Apple: "Pomme",
  Boot: "Botte",
  "A scanner is best for checking a control panel.":
    "Un scanner est le meilleur outil pour vérifier un panneau de contrôle.",
  "You collect magic crystals for a spell.":
    "Tu collectes des cristaux magiques pour un sort.",
  "You collect 3 crystals. Each crystal has 2 magic points. Total power?":
    "Tu collectes 3 cristaux. Chaque cristal vaut 2 points de magie. Puissance totale ?",
  "3 × 2 = 6 magic points.": "3 × 2 = 6 points de magie.",
  "You mix potion ingredients.": "Tu mélanges des ingrédients de potion.",
  "Each potion needs 4 herbs. You make 2 potions. How many herbs?":
    "Chaque potion nécessite 4 herbes. Tu prépares 2 potions. Combien d'herbes ?",
  "4 × 2 = 8 herbs.": "4 × 2 = 8 herbes.",
  "A wizard gives you a clue.": "Un sorcier te donne un indice.",
  "If the silver key opens the library and you need the library, which key should you take?":
    "Si la clé en argent ouvre la bibliothèque et que tu dois y entrer, quelle clé dois-tu prendre ?",
  "You need the key that opens the library.":
    "Tu dois prendre la clé qui ouvre la bibliothèque.",
  "Magic scrolls contain spell symbols.":
    "Les parchemins magiques contiennent des symboles de sorts.",
  "Each scroll has 5 symbols. You read 6 scrolls. Total symbols?":
    "Chaque parchemin a 5 symboles. Tu lis 6 parchemins. Total de symboles ?",
  "5 × 6 = 30 symbols.": "5 × 6 = 30 symboles.",
  "Spell lights flash in a magical sequence.":
    "Les lumières des sorts clignotent selon une séquence magique.",
  "What comes next? 1, 2, 4, 8, ?": "Quel nombre vient ensuite ? 1, 2, 4, 8, ?",
  "A potion changes color when heated.":
    "Une potion change de couleur lorsqu'elle est chauffée.",
  "What most likely caused the color change?":
    "Quelle cause a le plus probablement provoqué ce changement de couleur ?",
  "The clue says the potion changed when heated.":
    "L'indice indique que la potion a changé lorsqu'elle a été chauffée.",
  "Magic crystals power the portal.":
    "Des cristaux magiques alimentent le portail.",
  "Each crystal gives 8 energy. You have 6 crystals. Total energy?":
    "Chaque cristal fournit 8 points d'énergie. Tu as 6 cristaux. Énergie totale ?",
  "6 × 8 = 48 energy.": "6 × 8 = 48 points d'énergie.",
  "The portal opens only if the moon rune is active. The moon rune is not active.":
    "Le portail s'ouvre seulement si la rune de lune est active. La rune de lune n'est pas active.",
  "What can you conclude?": "Que peux-tu conclure ?",
  "If the needed rune is not active, the portal cannot open yet.":
    "Si la rune nécessaire n'est pas active, le portail ne peut pas encore s'ouvrir.",
  "You can prepare only one item before entering the portal.":
    "Tu ne peux préparer qu'un seul objet avant d'entrer dans le portail.",
  "Which is the smartest choice?": "Quel est le choix le plus intelligent ?",
  "A spell book is the most useful item for a magical portal.":
    "Un livre de sorts est l'objet le plus utile pour un portail magique.",
  "You collect food supplies for survivors.":
    "Tu récupères des réserves de nourriture pour les survivants.",
  "You find 4 boxes. Each box has 3 food packs. Total packs?":
    "Tu trouves 4 boîtes. Chaque boîte contient 3 rations. Total de rations ?",
  "4 × 3 = 12 packs.": "4 × 3 = 12 rations.",
  "You count survivors in the shelter.":
    "Tu comptes les survivants dans l'abri.",
  "Each room has 2 survivors. There are 6 rooms. Total survivors?":
    "Chaque pièce abrite 2 survivants. Il y a 6 pièces. Total de survivants ?",
  "6 × 2 = 12 survivors.": "6 × 2 = 12 survivants.",
  "The shelter needs help quickly.": "L'abri a besoin d'aide rapidement.",
  "What is the best first step?": "Quelle est la meilleure première étape ?",
  "Count supplies and people": "Compter les provisions et les personnes",
  "Turn off all lights and sleep": "Éteindre toutes les lumières et dormir",
  "Break the door": "Casser la porte",
  "Good survival planning starts with knowing your resources.":
    "Une bonne planification de survie commence par connaître ses ressources.",
  "You see zombie groups in the street.":
    "Tu vois des groupes de zombies dans la rue.",
  "There are 5 groups. Each group has 4 zombies. Total zombies?":
    "Il y a 5 groupes. Chaque groupe compte 4 zombies. Total de zombies ?",
  "5 × 4 = 20 zombies.": "5 × 4 = 20 zombies.",
  "The map says the safe road is not blocked and not noisy.":
    "La carte indique que la route sûre n'est ni bloquée ni bruyante.",
  "Which road is safest?": "Quelle route est la plus sûre ?",
  "The safe road must be open and quiet.":
    "La route sûre doit être dégagée et calme.",
  "You compare two supply options.": "Tu compares deux options de ravitaillement.",
  "Which gives more food: 4 boxes with 5 packs each, or 3 boxes with 7 packs each?":
    "Quelle option fournit le plus de nourriture : 4 boîtes de 5 rations chacune, ou 3 boîtes de 7 rations chacune ?",
  "4×5=20, while 3×7=21.": "4×5=20, tandis que 3×7=21.",
  "You load supplies onto the escape bus.":
    "Tu charges des provisions dans le bus d'évacuation.",
  "Each box holds 8 food packs. There are 6 boxes. Total packs?":
    "Chaque boîte contient 8 rations. Il y a 6 boîtes. Total de rations ?",
  "6 × 8 = 48 packs.": "6 × 8 = 48 rations.",
  "The escape gate opens only if the power is on and the lock code is correct. The power is off.":
    "La porte de sortie s'ouvre seulement si l'alimentation est active et si le code est correct. L'alimentation est coupée.",
  "What is true right now?": "Que sait-on avec certitude pour l'instant ?",
  "If the power is off, the gate cannot open yet.":
    "Si l'alimentation est coupée, la porte ne peut pas encore s'ouvrir.",
  "You can carry only one thing to the bus.":
    "Tu ne peux emporter qu'un seul objet jusqu'au bus.",
  "Food is the most useful choice for survival.":
    "La nourriture est le choix le plus utile pour survivre."
};

const NEW_COPY_FR = {
  // Chapter intro + world card copy
  "Space Exploration": "Exploration spatiale",
  "Urban Sci-Fi": "Sci-Fi urbaine",
  "Wildlife Adventure": "Aventure animalière",
  "Farm Life": "Vie à la ferme",
  "Shadow Escape": "Fuite dans la brume",
  "Magic Academy": "Académie de magie",
  "The First Light": "La première lueur",
  "The Fading City": "La ville qui s'éteint",
  "The Silent Reserve": "La réserve silencieuse",
  "The Quiet Fields": "Les champs silencieux",
  "The Gray City": "La cité grise",
  "The Last Gate": "La dernière porte",
  "Follow the first broken signal.": "Suis le premier signal brisé.",
  "Enter the city that is losing power.": "Entre dans la ville qui perd son énergie.",
  "Follow the animals into silence.": "Suis les animaux vers le silence.",
  "Help the land remember how to grow.": "Aide la terre à se souvenir comment nourrir la vie.",
  "Guide the lost through the gray mist.": "Guide les perdus à travers la brume grise.",
  "Open the final gate.": "Ouvre la porte finale.",
  "A silent planet. A sleeping reactor. The journey begins.":
    "Une planète silencieuse. Un réacteur endormi. L'aventure commence.",
  "Lights flicker. Systems fail. Something deeper is wrong.":
    "Les lumières vacillent. Les systèmes cèdent. Quelque chose de plus profond se dérègle.",
  "The forest is uneasy, and the lake is waiting.":
    "La forêt est inquiète, et le lac attend.",
  "The soil is tired, but the farm is still holding on.":
    "La terre est épuisée, mais la ferme tient encore debout.",
  "The city is not full of monsters. It is full of people who have forgotten the way.":
    "La ville n'est pas remplie de monstres. Elle est remplie de personnes qui ont oublié le chemin.",
  "The fragments are ready. The Bridge is waiting.":
    "Les fragments sont prêts. Le Pont attend.",
  "Trace the first signal through alien ruins.":
    "Suis le premier signal à travers les ruines extraterrestres.",
  "Restore power in a city that is fading.":
    "Rétablis l'énergie dans une ville qui s'éteint.",
  "Find out why the animals are gathering.":
    "Découvre pourquoi les animaux se rassemblent.",
  "Help a struggling farm wake the land.":
    "Aide une ferme en difficulté à réveiller la terre.",
  "Lead survivors through the gray mist.":
    "Guide les survivants à travers la brume grise.",
  "Unlock the final secret of the Bridge.":
    "Déverrouille le secret final du Pont.",

  // New world story copy
  "Your spaceship crash-lands on a silent alien planet. A tiny glowing guide named Lumi appears and tells you that the Wonder Bridge is breaking apart. Your engine is dead, and the first clue is hidden somewhere nearby.":
    "Ton vaisseau s'ecrase sur une planete extraterrestre silencieuse. Un petit guide lumineux nomme Lumi apparait et te dit que le Wonder Bridge se disloque. Ton moteur est hors service, et le premier indice est cache quelque part tout pres.",
  "Inside the cave, floating crystals pulse like slow heartbeats. Lumi says this light feels older than your ship — like part of a forgotten signal system.":
    "Dans la grotte, des cristaux flottants pulsent comme de lents battements de coeur. Lumi dit que cette lumiere semble plus ancienne que ton vaisseau — comme une partie d'un ancien systeme de signal oublie.",
  "The alien forest is alive with moving vines and soft blue leaves. Some of the plants seem to lean in one direction, as if they are guiding you toward something important.":
    "La foret extraterrestre est vivante, avec des lianes mouvantes et des feuilles bleues et douces. Certaines plantes semblent s'incliner dans une meme direction, comme si elles te guidaient vers quelque chose d'important.",
  "The cave symbols describe an ancient energy network once used to connect distant worlds. A broken map appears in the pattern, pointing toward old ruins beyond the ridge.":
    "Les symboles de la grotte decrivent un ancien reseau d'energie autrefois utilise pour relier des mondes lointains. Une carte brisee apparait dans le motif et pointe vers de vieilles ruines au-dela de la crete.",
  "You reach ancient alien ruins built around a dormant signal tower. Mirror panels, cracked pathways, and a faint hum suggest the place could still wake up.":
    "Tu atteins d'anciennes ruines extraterrestres construites autour d'une tour de signal endormie. Des panneaux miroirs, des passages fissures et un leger bourdonnement laissent penser que l'endroit pourrait encore se reveiller.",
  "Behind the ruins, you discover an ancient energy reactor. If it restarts, your ship may fly again — and Lumi believes it will reveal where the next missing fragment has gone.":
    "Derriere les ruines, tu decouvres un ancien reacteur d'energie extraterrestre. S'il redemarre, ton vaisseau pourra peut-etre revoler — et Lumi pense qu'il revelera ou se trouve le prochain fragment manquant.",
  "When the reactor comes online, a hologram points you to Neon Harbor, a futuristic city losing power one block at a time. Lumi says the second fragment may be hidden where the city thinks for itself.":
    "Quand le reacteur se remet en marche, un hologramme t'indique Neon Harbor, une ville futuriste qui perd son energie quartier apres quartier. Lumi pense que le deuxieme fragment est cache la ou la ville pense par elle-meme.",
  "Inside the robot factory, machines are still building, but slower than they should. Some assembly lines have stopped completely, while others keep repeating the same broken command.":
    "Dans l'usine de robots, les machines construisent encore, mais plus lentement qu'elles ne le devraient. Certaines chaines d'assemblage sont a l'arret complet, tandis que d'autres repetent sans fin la meme commande defectueuse.",
  "At the hover train station, service bots keep rerouting passengers unevenly. Busy districts stay bright, while family neighborhoods flicker and wait in the dark.":
    "A la station des trains aeriens, les robots de service redirigent les passagers de facon inegale. Les quartiers animes restent lumineux, tandis que les zones familiales vacillent et attendent dans le noir.",
  "The city control center shows something strange: the power problem is not only inside the city. Wildlife routes, weather signals, and traffic systems are all drifting together, as if one hidden force is pulling everything off balance.":
    "Le centre de controle de la ville revele quelque chose d'etrange : le probleme d'energie n'est pas seulement interne a la ville. Les routes animales, les signaux meteo et les systemes de trafic derivent ensemble, comme si une force cachee tirait tout hors d'equilibre.",
  "At the center of the city lies the AI energy core. Lumi recognizes the same pulse you saw on the alien planet. If you stabilize it, the city may reveal where the third fragment has gone.":
    "Au coeur de la ville se trouve le noyau d'energie IA. Lumi reconnait la meme pulsation que sur la planete extraterrestre. Si tu le stabilises, la ville pourrait reveler ou est parti le troisieme fragment.",
  "The city core projects a map of a wildlife reserve. Animal paths from every direction are bending toward one quiet lake. Lumi says nature may be feeling the broken bridge before anyone else.":
    "Le noyau de la ville projette la carte d'une reserve animaliere. Les trajectoires animales de toutes les directions convergent vers un lac silencieux. Lumi dit que la nature ressent peut-etre la rupture du Pont avant tout le monde.",
  "You follow fresh tracks to a riverbank where deer stand alert and silent. They are not grazing. They are watching the woods, as if waiting for a signal that has not come.":
    "Tu suis des traces fraiches jusqu'a une rive ou des cerfs restent immobiles et alertes. Ils ne broutent pas. Ils regardent la foret, comme s'ils attendaient un signal qui n'est jamais venu.",
  "From the hill, you can see birds, deer, and smaller animals all moving in unusual patterns. They are not fleeing randomly. Somehow, they seem to be searching for the same place.":
    "Depuis la colline, tu vois des oiseaux, des cerfs et de plus petits animaux se deplacer selon des schemas inhabituels. Ils ne fuient pas au hasard. D'une certaine facon, ils semblent chercher le meme endroit.",
  "Inside the forest, monkeys leap from branch to branch above you while the lower paths stay strangely empty. Even the wind feels careful here, like the whole reserve is holding its breath.":
    "Dans la foret, des singes bondissent de branche en branche au-dessus de toi, tandis que les sentiers du bas restent etrangement vides. Meme le vent parait prudent, comme si toute la reserve retenait son souffle.",
  "You arrive at a peaceful lake where animals from different habitats gather together without fear. Under an old tree near the water, Lumi senses a sleeping fragment hidden beneath the roots.":
    "Tu arrives a un lac paisible ou des animaux d'habitats differents se rassemblent sans crainte. Sous un vieil arbre au bord de l'eau, Lumi sent un fragment endormi cache sous les racines.",
  "Beneath the tree roots, you find a message: 'When the ground forgets how to grow, seek the hands that still care for it.' Lumi leads you to a farm where the soil has grown cold and quiet.":
    "Sous les racines de l'arbre, tu trouves un message : 'Quand la terre oublie comment faire pousser, cherche les mains qui continuent d'en prendre soin.' Lumi te conduit vers une ferme ou le sol est devenu froid et silencieux.",
  "The cows and sheep are waiting, but something feels off. They are calm, yet restless, as if they can sense a storm that has not arrived yet.":
    "Les vaches et les moutons attendent, mais quelque chose cloche. Ils sont calmes, pourtant agites, comme s'ils sentaient une tempete qui n'est pas encore arrivee.",
  "The apple orchard is full, but the leaves look tired and the ground is too dry in some rows and too soft in others. The farm still works, but only because people are working harder than the land itself.":
    "Le verger est plein, mais les feuilles semblent fatiguees et le sol est trop sec par endroits et trop mou ailleurs. La ferme tient encore, mais seulement parce que les gens travaillent plus dur que la terre elle-meme.",
  "Inside the barn, you find tools, baskets, and several families who arrived from the city. They speak of a gray shadow spreading through the streets and forcing people to abandon their homes.":
    "Dans la grange, tu trouves des outils, des paniers et plusieurs familles arrivees de la ville. Elles parlent d'une ombre grise qui se propage dans les rues et force les habitants a abandonner leur maison.",
  "At sunset, the old windmill finally turns again, and a warm pulse moves through the field. The fourth fragment wakes up — but in the distance, a column of gray mist rises above the city.":
    "Au coucher du soleil, le vieux moulin se remet enfin a tourner, et une pulsation chaude traverse le champ. Le quatrieme fragment s'eveille — mais au loin, une colonne de brume grise s'eleve au-dessus de la ville.",
  "You reach the edge of the city and see the gray mist for yourself. The people wandering through it do not seem like monsters. They look lost, as if the shadow has stolen their memory of where they belong.":
    "Tu atteins la lisiere de la ville et vois la brume grise de tes propres yeux. Les personnes qui y errent ne ressemblent pas a des monstres. Elles semblent perdues, comme si l'ombre leur avait vole la memoire de l'endroit auquel elles appartiennent.",
  "Inside the house, you find food, blankets, flashlights, and notes from a family that left in a hurry. One line is underlined twice: 'The school is still safe. For now.'" :
    "Dans la maison, tu trouves de la nourriture, des couvertures, des lampes torches, et des notes d'une famille partie en urgence. Une phrase est soulignee deux fois : 'L'ecole est encore sure. Pour l'instant.'",
  "The street is almost silent except for distant footsteps in the mist. Shapes drift between the buildings, slow and confused. They are not chasing anyone. They are following something unseen.":
    "La rue est presque silencieuse, sauf quelques pas lointains dans la brume. Des silhouettes glissent entre les batiments, lentes et confuses. Elles ne poursuivent personne. Elles suivent quelque chose d'invisible.",
  "The school has become a shelter full of frightened families. Some people want to leave immediately. Others want to stay hidden. Lumi discovers that the gray mist behaves less like a virus and more like a spreading emptiness.":
    "L'ecole est devenue un refuge rempli de familles effrayees. Certaines veulent partir tout de suite. D'autres veulent rester cachees. Lumi decouvre que la brume grise se comporte moins comme un virus que comme un vide qui s'etend.",
  "In the bus yard, you find transport, fuel, and an old school crest marked with the symbol of a magic academy. The fifth fragment is close — and the crest may point to the final world.":
    "Dans le depot de bus, tu trouves des vehicules, du carburant et un ancien blason scolaire marque du symbole d'une academie de magie. Le cinquieme fragment est proche — et ce blason pourrait indiquer le monde final.",
  "With the school crest in hand, you arrive at a silent magic academy. Lumi says this is where the Wonder Bridge was once watched over. If the final gate opens, every world may remember how to connect again.":
    "Avec le blason de l'ecole en main, tu arrives dans une academie de magie silencieuse. Lumi dit que c'est ici que le Wonder Bridge etait autrefois surveille. Si la porte finale s'ouvre, tous les mondes pourront peut-etre se souvenir comment se reconnecter.",
  "The library glows with old books and memory scrolls. They describe the Wonder Bridge not as a road, but as a living link between worlds, held together by trust, courage, and care.":
    "La bibliotheque brille de vieux livres et de parchemins de memoire. Ils decrivent le Wonder Bridge non comme une route, mais comme un lien vivant entre les mondes, maintenu par la confiance, le courage et l'attention portee aux autres.",
  "The potion laboratory still bubbles with leftover magic. On one table, you find notes about the gray mist: it does not destroy worlds — it makes them forget one another.":
    "Le laboratoire de potions bouillonne encore d'une magie residuelle. Sur une table, tu trouves des notes sur la brume grise : elle ne detruit pas les mondes — elle les pousse a s'oublier.",
  "At the top of the wizard tower, five fragments begin to glow together. The tower shakes softly, but one place in the portal ring is still empty.":
    "Au sommet de la tour des mages, les cinq fragments commencent a briller ensemble. La tour tremble doucement, mais un emplacement de l'anneau du portail reste vide.",
  "At the portal, Lumi finally understands the truth: the sixth fragment was never missing. It is awakened by the one who listened, explored, protected others, and chose to reconnect the worlds.":
    "Devant le portail, Lumi comprend enfin la verite : le sixieme fragment n'a jamais ete perdu. Il s'eveille chez celle ou celui qui a choisi d'ecouter, d'explorer, de proteger les autres et de reconnecter les mondes.",
  "Your ship has crash-landed on a distant alien world, and its engine is completely dead.":
    "Ton vaisseau s'est ecrase sur un monde extraterrestre lointain, et son moteur est completement hors service.",
  "A glowing guide named Lumi appears beside you and points toward an ancient signal hidden somewhere in the dark.":
    "Un guide lumineux nomme Lumi apparait a tes cotes et t'indique un ancien signal cache quelque part dans l'obscurite.",
  "The restored reactor sends you to Neon Harbor, a futuristic city where the lights are going out one district at a time.":
    "Le reacteur restaure t'envoie vers Neon Harbor, une ville futuriste ou les lumieres s'eteignent quartier apres quartier.",
  "Lumi believes the next fragment is hidden inside the mind of the city itself.":
    "Lumi pense que le fragment suivant est cache au coeur meme de l'intelligence de la ville.",
  "A map from the city points toward a wildlife reserve where animals from every direction are gathering in strange patterns.":
    "Une carte venue de la ville pointe vers une reserve animaliere ou des animaux de toutes directions se rassemblent selon d'etranges motifs.",
  "Lumi senses that nature has noticed the broken Bridge long before anyone else.":
    "Lumi sent que la nature a remarque le Pont brise bien avant tout le monde.",
  "A message beneath the old tree sends you to a farm where the earth feels cold and the windmill barely turns.":
    "Un message sous le vieil arbre te mene vers une ferme ou la terre parait froide et ou le moulin tourne a peine.",
  "Something is wrong with the land, and the people working here are trying to hold everything together.":
    "Quelque chose ne va pas avec la terre, et les personnes qui travaillent ici essaient de maintenir tout en equilibre.",
  "Beyond the fields, a heavy gray mist has swallowed the city streets and turned them into a maze of fear and confusion.":
    "Au-dela des champs, une epaisse brume grise a englouti les rues de la ville et les a transformees en un labyrinthe de peur et de confusion.",
  "Lumi discovers that the shadow does not simply attack — it makes people lose their sense of direction, memory, and belonging.":
    "Lumi decouvre que l'ombre ne se contente pas d'attaquer — elle fait perdre aux gens le sens de l'orientation, la memoire et le sentiment d'appartenance.",
  "With five fragments in hand, you arrive at a silent academy where the Wonder Bridge was once protected.":
    "Avec cinq fragments en main, tu arrives dans une academie silencieuse ou le Wonder Bridge etait autrefois protege.",
  "If the final portal can be opened, the worlds may remember how to connect again.":
    "Si le portail final peut etre ouvert, les mondes se souviendront peut-etre comment se reconnecter.",
  "1.2 km": "1,2 km",
  "1.8 km": "1,8 km",
  "2.5 km": "2,5 km",
  "3.6 km": "3,6 km",
  "960 m²": "960 m²",
  "1008 m²": "1008 m²",
  "1120 m²": "1120 m²",
  "1260 m²": "1260 m²",

  // Chapter completion copy
  "The First Light Returns": "Le retour de la première lueur",
  "A City Begins to Remember": "Une ville commence à se souvenir",
  "The Forest Was Listening": "La forêt écoutait",
  "The Land Takes a Breath": "La terre reprend son souffle",
  "Through the Gray Mist": "À travers la brume grise",
  "The Bridge Wakes": "Le Pont s'éveille",
  "You Are a Bridge Keeper": "Tu es un Gardien du Pont",
  "Chapter Complete: Space Exploration": "Chapitre terminé : Exploration spatiale",
  "Chapter Complete: Urban Sci-Fi": "Chapitre terminé : Sci-Fi urbaine",
  "Chapter Complete: Wildlife Adventure": "Chapitre terminé : Aventure animalière",
  "Chapter Complete: Farm Life": "Chapitre terminé : Vie à la ferme",
  "Chapter Complete: Shadow Escape": "Chapitre terminé : Fuite dans la brume",
  "Chapter Complete: Magic Academy": "Chapitre terminé : Académie de magie",
  "Next destination: a city that has forgotten how to stay bright.":
    "Prochaine destination : une ville qui a oublié comment rester lumineuse.",
  "Next destination: a reserve where the animals are gathering in silence.":
    "Prochaine destination : une réserve où les animaux se rassemblent en silence.",
  "Next destination: a farm where the land is growing quiet.":
    "Prochaine destination : une ferme où la terre devient silencieuse.",
  "Next destination: a city swallowed by gray shadow.":
    "Prochaine destination : une ville engloutie par une ombre grise.",
  "Next destination: the magic academy where the Wonder Bridge was once guarded.":
    "Prochaine destination : l'académie de magie où le Wonder Bridge était autrefois protégé.",
  "The story is complete — but the Bridge is open again, and more journeys can begin.":
    "L'histoire est complète — mais le Pont est à nouveau ouvert, et d'autres voyages peuvent commencer.",
  "More stories are waiting on the other side.":
    "D'autres histoires t'attendent de l'autre côté.",
  "Thank you, Bridge Keeper. When worlds begin to drift again, the Bridge will know how to find you.":
    "Merci, Gardien du Pont. Quand les mondes recommenceront à dériver, le Pont saura comment te retrouver.",
  "We did not just fix a machine. We woke up the first memory of the Bridge.":
    "Nous n'avons pas seulement réparé une machine. Nous avons réveillé la première mémoire du Pont.",
  "Machines can measure the damage. Nature can feel the pain first.":
    "Les machines mesurent les dégâts. La nature ressent la douleur en premier.",
  "The forest did not speak with words. It spoke by waiting for someone to notice.":
    "La forêt n'a pas parlé avec des mots. Elle a parlé en attendant que quelqu'un remarque.",
  "We helped the land remember how to breathe. Now we have to help people remember how to find their way back.":
    "Nous avons aidé la terre à se souvenir comment respirer. Maintenant, nous devons aider les gens à se souvenir du chemin du retour.",
  "This shadow was never only trying to scare people. It was trying to make them forget who they were, and where they belonged.":
    "Cette ombre n'a jamais seulement cherché à effrayer. Elle cherchait à faire oublier aux gens qui ils étaient et où ils avaient leur place.",
  "The final fragment was never an object. It was the choice to connect worlds that had begun to forget each other.":
    "Le fragment final n'a jamais été un objet. C'était le choix de reconnecter des mondes qui commençaient à s'oublier.",
  "The ancient reactor hums back to life, and blue light rushes through the ruins. A hidden signal rises into the sky, carrying the shape of a distant city losing power block by block. Lumi drifts beside you and whispers that this was never only about your crashed ship. The first fragment is awake, and the next world is already calling.":
    "L'ancien réacteur se remet à vibrer, et une lumière bleue traverse les ruines. Un signal caché monte vers le ciel, dessinant la silhouette d'une ville lointaine qui perd son énergie quartier après quartier. Lumi flotte près de toi et murmure que cela n'a jamais concerné seulement ton vaisseau écrasé. Le premier fragment est éveillé, et le monde suivant t'appelle déjà.",
  "The AI core stabilizes, and Neon Harbor lights up again in waves of electric blue. Screens across the control tower fill with maps, weather patterns, and animal routes bending in the same strange direction. The city was not the cause of the imbalance. It was only one of the first places to feel it. As the second fragment rises from the core, Lumi turns the wildlife map toward you.":
    "Le noyau IA se stabilise, et Neon Harbor se rallume par vagues de bleu électrique. Les écrans de la tour de contrôle se remplissent de cartes, de motifs météo et d'itinéraires animaliers qui dévient tous dans la même direction étrange. La ville n'était pas la cause du déséquilibre. Elle fut seulement l'un des premiers lieux à en ressentir les effets. Quand le deuxième fragment s'élève du noyau, Lumi tourne vers toi la carte de la réserve.",
  "Beneath the roots of the old lakeside tree, the third fragment glows like a sleeping seed finally touched by sunlight. Around the lake, animals from different habitats begin to settle and drift apart, no longer frozen by the same uneasy waiting. Carved into a hidden stone, you find a message: 'When the ground forgets how to grow, seek the hands that still care for it.' Lumi reads it twice before looking toward the distant fields.":
    "Sous les racines du vieil arbre au bord du lac, le troisième fragment brille comme une graine endormie enfin touchée par la lumière. Autour du lac, des animaux d'habitats différents commencent à se calmer et à se disperser, libérés de la même attente inquiète. Gravé dans une pierre cachée, tu trouves un message : 'Quand la terre oublie comment faire pousser, cherche les mains qui continuent d'en prendre soin.' Lumi le relit deux fois avant de regarder vers les champs au loin.",
  "The old windmill groans, then turns with steady strength, and a warm pulse moves through the fields. The fourth fragment shines out from the farm like a small harvest star. But before the feeling of relief can settle, everyone looks up. Far beyond the orchard, a gray column of mist rises over the city. Families who fled there were right to be afraid. Something worse is spreading now.":
    "Le vieux moulin grince, puis tourne avec une force régulière, et une pulsation chaude traverse les champs. Le quatrième fragment rayonne au-dessus de la ferme comme une petite étoile de récolte. Mais avant que le soulagement ne s'installe, tout le monde lève les yeux. Bien au-delà du verger, une colonne de brume grise s'élève au-dessus de la ville. Les familles qui en ont fui avaient raison d'avoir peur. Quelque chose de pire est en train de se propager.",
  "The last bus pulls away from the school, carrying families out of the fog. Behind you, the gray mist shifts and curls, not like a monster defeated, but like an emptiness losing its grip. In the bus yard, the fifth fragment and an old academy crest are found together, as if they had been waiting for the same hands to reach them. Lumi studies the crest and finally understands where the road ends.":
    "Le dernier bus quitte l'école, emmenant des familles hors du brouillard. Derrière toi, la brume grise se tord et se replie, non comme un monstre vaincu, mais comme un vide qui perd son emprise. Dans le dépôt de bus, le cinquième fragment et un ancien blason d'académie sont retrouvés ensemble, comme s'ils attendaient les mêmes mains. Lumi observe le blason et comprend enfin où mène la route.",
  "Inside the academy portal, the five fragments blaze together, and the missing sixth awakens in response to you. Not hidden in a vault, not lost in a ruin, but waiting in the one who chose to listen, explore, protect, and reconnect. Light pours through the portal ring. Across every world, systems restart, fields soften, forests settle, and the gray mist begins to fold back into silence. The Wonder Bridge lives again.":
    "Dans le portail de l'académie, les cinq fragments s'embrasent ensemble, et le sixième manquant s'éveille en réponse à toi. Il n'était ni caché dans un coffre, ni perdu dans une ruine, mais en attente chez celui qui choisit d'écouter, d'explorer, de protéger et de reconnecter. La lumière traverse l'anneau du portail. Dans chaque monde, les systèmes redémarrent, les champs se détendent, les forêts s'apaisent, et la brume grise commence à se replier dans le silence. Le Wonder Bridge vit à nouveau.",
  "You followed the first signal into a broken alien ruin and helped restore an ancient reactor. In doing so, you recovered the first fragment of the Wonder Bridge and discovered that the trouble is spreading far beyond one world. This journey is no longer about escape. It is about connection.":
    "Tu as suivi le premier signal jusqu'à une ruine extraterrestre brisée et aidé à restaurer un ancien réacteur. Ce faisant, tu as récupéré le premier fragment du Wonder Bridge et découvert que le problème se propage bien au-delà d'un seul monde. Ce voyage ne parle plus d'évasion. Il parle de connexion.",
  "You entered a failing futuristic city and helped restore its energy core. The second fragment revealed that the problem is not limited to power systems or broken machines. The imbalance is spreading across worlds and into living ecosystems. The trail now leads into the wild.":
    "Tu es entré dans une ville futuriste en panne et tu as aidé à restaurer son noyau énergétique. Le deuxième fragment a révélé que le problème ne se limite pas aux réseaux d'énergie ni aux machines défaillantes. Le déséquilibre se propage entre les mondes et jusque dans les écosystèmes vivants. La piste mène maintenant vers la nature.",
  "You tracked strange animal migrations through the reserve and discovered that nature itself was reacting to the broken Bridge. At the silent lake, you recovered the third fragment and uncovered a clue pointing toward farmland and human care. The journey now moves from wilderness into everyday life.":
    "Tu as suivi d'étranges migrations animales dans la réserve et découvert que la nature elle-même réagissait au Pont brisé. Au lac silencieux, tu as récupéré le troisième fragment et trouvé un indice qui pointe vers les terres agricoles et le soin humain. Le voyage passe maintenant du sauvage au quotidien.",
  "You helped care for animals, organize supplies, and restore life to a struggling farm. The fourth fragment awakened when the land finally responded, but the victory was short-lived. Refugees from the city brought news of a gray mist that steals direction, memory, and calm. The next chapter is the most dangerous yet.":
    "Tu as aidé à soigner les animaux, organiser les réserves et redonner vie à une ferme en difficulté. Le quatrième fragment s'est éveillé quand la terre a enfin répondu, mais la victoire fut brève. Des réfugiés de la ville ont apporté des nouvelles d'une brume grise qui vole l'orientation, la mémoire et le calme. Le chapitre suivant est le plus dangereux jusqu'ici.",
  "You entered the gray city, protected survivors, and guided them toward safety. The fifth fragment revealed that the spreading shadow is less like a virus and more like a growing emptiness that severs people from memory and belonging. With the academy crest in hand, the path to the final world is finally clear.":
    "Tu es entré dans la cité grise, protégé les survivants et les as guidés vers la sécurité. Le cinquième fragment a révélé que l'ombre qui se propage ressemble moins à un virus qu'à un vide grandissant qui coupe les gens de leur mémoire et de leur sentiment d'appartenance. Avec le blason de l'académie en main, le chemin vers le monde final devient enfin clair.",
  "You reached the silent academy and uncovered the truth behind the Wonder Bridge. The final fragment was not something to collect, but something to awaken through courage, care, and connection. With the Bridge restored, all six worlds begin to remember one another, and the journey becomes something larger than survival: it becomes stewardship.":
    "Tu as atteint l'académie silencieuse et découvert la vérité derrière le Wonder Bridge. Le fragment final n'était pas quelque chose à collecter, mais quelque chose à éveiller par le courage, le soin et la connexion. Avec le Pont restauré, les six mondes commencent à se souvenir les uns des autres, et le voyage devient plus grand que la survie : il devient une responsabilité.",
  "You followed broken signals across six worlds. You restored power, listened to the wild, cared for the land, protected the lost, and reopened the final gate. The Wonder Bridge is alive again because you chose not only to solve problems, but to reconnect what had been drifting apart. The worlds are awake. Their stories are no longer separate. And now, they remember you.":
    "Tu as suivi des signaux brisés à travers six mondes. Tu as rétabli l'énergie, écouté le vivant, pris soin de la terre, protégé les perdus, et rouvert la porte finale. Le Wonder Bridge est vivant à nouveau parce que tu as choisi non seulement de résoudre des problèmes, mais de reconnecter ce qui dérivait. Les mondes sont éveillés. Leurs histoires ne sont plus séparées. Et maintenant, ils se souviennent de toi.",

  // Boss copy
  "Reactor Lock": "Verrou du réacteur",
  "Core Stabilizer": "Stabilisateur du noyau",
  "Lakewatch Trial": "Épreuve du lac",
  "Harvest Engine": "Mécanisme de récolte",
  "Last Bus Plan": "Plan du dernier bus",
  "Portal Ring": "Anneau du portail",
  "The ancient reactor is sealed behind a final control puzzle.":
    "L'ancien réacteur est scellé derrière une ultime énigme de contrôle.",
  "The city AI core is unstable, and the whole grid depends on your next move.":
    "Le noyau IA de la ville est instable, et tout le réseau dépend de ton prochain choix.",
  "The animals have gathered at the lake for a reason. You must understand the pattern before it disappears.":
    "Les animaux se sont rassemblés au lac pour une raison. Tu dois comprendre le motif avant qu'il ne disparaisse.",
  "The farm can still survive, but only if you restore order before everything gives out.":
    "La ferme peut encore tenir, mais seulement si tu rétablis l'ordre avant que tout ne cède.",
  "The shelter cannot hold forever. You need one clear plan to get people out.":
    "L'abri ne tiendra pas éternellement. Il faut un plan clair pour faire sortir les gens.",
  "Five fragments are ready, but the final gate will only open for the one who can reconnect the worlds.":
    "Cinq fragments sont prêts, mais la porte finale ne s'ouvrira que pour celle ou celui qui saura reconnecter les mondes.",
  "Restore power to the alien ruins and awaken the first fragment.":
    "Rétablis l'énergie dans les ruines extraterrestres et réveille le premier fragment.",
  "Balance the system and bring Neon Harbor back online.":
    "Équilibre le système et remets Neon Harbor en ligne.",
  "Read the signs of the reserve and uncover the hidden fragment.":
    "Interprète les signes de la réserve et révèle le fragment caché.",
  "Help the land recover and secure the fourth fragment.":
    "Aide la terre à se rétablir et sécurise le quatrième fragment.",
  "Guide the survivors through the gray mist and secure safe escape.":
    "Guide les survivants à travers la brume grise et assure une évacuation sûre.",
  "Activate the portal and restore the Wonder Bridge.":
    "Active le portail et restaure le Wonder Bridge.",
  "Reactor Restored": "Réacteur restauré",
  "Core Stabilized": "Noyau stabilisé",
  "The Lake Answered": "Le lac a répondu",
  "The Fields Breathe Again": "Les champs respirent à nouveau",
  "Escape Secured": "Évacuation sécurisée",
  "A new destination has appeared: Neon Harbor.":
    "Une nouvelle destination est apparue : Neon Harbor.",
  "A wildlife reserve has been marked as your next route.":
    "Une réserve naturelle a été indiquée comme prochaine route.",
  "The trail now leads to a struggling farm.":
    "La piste mène maintenant vers une ferme en difficulté.",
  "The next chapter begins inside the shadowed city.":
    "Le prochain chapitre commence au cœur de la ville assombrie.",
  "The crest points to the Magic Academy.":
    "Le blason pointe vers l'Académie de magie.",
  "You have become a Bridge Keeper.":
    "Tu es devenu un Gardien du Pont.",
  "The reactor roars back to life, and blue energy floods the ruins. Your ship systems begin to recover, but more importantly, the first fragment of the Wonder Bridge finally wakes. A hidden signal rises into the sky, pointing toward a city losing power far away.":
    "Le réacteur rugit à nouveau, et une énergie bleue inonde les ruines. Les systèmes de ton vaisseau commencent à récupérer, mais surtout, le premier fragment du Wonder Bridge s'éveille enfin. Un signal caché monte dans le ciel et pointe vers une ville lointaine en perte d'énergie.",
  "The energy core steadies, and light spreads through the city block by block. But the system logs reveal something bigger: the trouble is not trapped inside the city. Wildlife routes and natural patterns are shifting too, as if the worlds are pulling away from one another.":
    "Le noyau énergétique se stabilise, et la lumière se propage dans la ville bloc par bloc. Mais les journaux système révèlent quelque chose de plus vaste : le problème n'est pas limité à la ville. Les routes animales et les motifs naturels se déplacent aussi, comme si les mondes s'éloignaient les uns des autres.",
  "The waiting ends. Beneath the roots of the old tree, the third fragment begins to glow like a waking seed. The animals slowly settle, and a message carved in stone points you toward the next truth: when the land begins to fail, look for the people still caring for it.":
    "L'attente se termine. Sous les racines du vieil arbre, le troisième fragment commence à briller comme une graine qui s'éveille. Les animaux se calment peu à peu, et un message gravé dans la pierre te montre la vérité suivante : quand la terre commence à faiblir, cherche les personnes qui continuent d'en prendre soin.",
  "The windmill turns, the fields answer, and the fourth fragment shines out over the farm like a harvest star. For one moment, everything feels steady again. Then the horizon darkens. A gray column of mist rises over the city, and everyone knows the real crisis has arrived.":
    "Le moulin tourne, les champs répondent, et le quatrième fragment rayonne sur la ferme comme une étoile de récolte. Pendant un instant, tout semble stable à nouveau. Puis l'horizon s'assombrit. Une colonne de brume grise s'élève au-dessus de la ville, et tout le monde comprend que la véritable crise est arrivée.",
  "The final bus pulls away, carrying families out of danger. The mist twists behind you, weaker now, as if losing its hold on the people it tried to erase. In the bus yard, you recover the fifth fragment and an old academy crest — the clearest sign yet that the final answer lies elsewhere.":
    "Le dernier bus démarre, emmenant les familles hors de danger. La brume se tord derrière toi, plus faible désormais, comme si elle perdait son emprise sur celles et ceux qu'elle tentait d'effacer. Dans le dépôt, tu récupères le cinquième fragment et un ancien blason d'académie — le signe le plus clair jusqu'ici que la réponse finale se trouve ailleurs.",
  "Light races through the portal ring as the final fragment awakens in response to you. Across every world, systems recover, forests settle, fields soften, and the gray mist begins to fold back into silence. The Wonder Bridge is alive again — not because you collected power, but because you restored connection.":
    "La lumière traverse l'anneau du portail tandis que le fragment final s'éveille en réponse à toi. Dans chaque monde, les systèmes se rétablissent, les forêts s'apaisent, les champs s'adoucissent, et la brume grise commence à se replier dans le silence. Le Wonder Bridge est vivant à nouveau — non parce que tu as accumulé du pouvoir, mais parce que tu as restauré la connexion.",

  // Mini boss copy
  "Restart the ancient reactor.": "Redémarre l'ancien réacteur.",
  "The first fragment is awake.": "Le premier fragment est éveillé.",
  "Balance the city's failing core.": "Stabilise le noyau défaillant de la ville.",
  "Neon Harbor shines again.": "Neon Harbor brille à nouveau.",
  "Read the pattern at the silent lake.": "Lis le motif du lac silencieux.",
  "The forest reveals its fragment.": "La forêt révèle son fragment.",
  "Restore order to the struggling farm.": "Rétablis l'ordre dans la ferme en difficulté.",
  "The land begins to breathe again.": "La terre recommence à respirer.",
  "Lead the survivors to safety.": "Conduis les survivants vers la sécurité.",
  "The fifth fragment is secured.": "Le cinquième fragment est sécurisé.",
  "The Wonder Bridge lives again.": "Le Wonder Bridge vit à nouveau."
};

function autoFrenchFallback(text) {
  if (!text) return text;
  const replacements = [
    [/Your/g, "Votre"],
    [/You/g, "Vous"],
    [/you/g, "vous"],
    [/What is/g, "Combien font"],
    [/Which is/g, "Lequel est"],
    [/Which/g, "Lequel"],
    [/How many/g, "Combien de"],
    [/Total/g, "Total"],
    [/energy/g, "énergie"],
    [/world/g, "monde"],
    [/story/g, "histoire"],
    [/question/g, "question"],
    [/answer/g, "réponse"],
    [/correct/g, "correcte"],
    [/safe/g, "sûr"],
    [/risk/g, "risque"],
    [/logic/g, "logique"],
    [/science/g, "science"],
    [/pattern/g, "motif"],
    [/city/g, "ville"],
    [/farm/g, "ferme"],
    [/forest/g, "forêt"],
    [/robot/g, "robot"],
    [/planet/g, "planète"]
  ];
  return replacements.reduce((acc, [pattern, replacement]) => acc.replace(pattern, replacement), text);
}

function readJsonFromStorage(key, fallbackValue) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return fallbackValue;
    }
    const parsed = JSON.parse(raw);
    return parsed ?? fallbackValue;
  } catch {
    return fallbackValue;
  }
}

function getQuestionChoiceKey(worldId, nodeId, choiceText) {
  return `${worldId || "none"}::${nodeId || "none"}::${choiceText || "choice"}`;
}

export default function App() {
  const saved = loadProgress();
  const initialLang = ["en", "fr", "zh"].includes(saved.lang) ? saved.lang : "en";
  const runtimeHostname = getRuntimeHostname();
  const standaloneProductKey = getStandaloneProductKey(runtimeHostname);
  const standaloneEntryPage = standaloneProductKey
    ? STANDALONE_PRODUCT_ENTRY_PAGES[standaloneProductKey]
    : null;
  const isStandaloneEchoHost = standaloneProductKey === "pagetone";
  const canUseStandaloneProductUrls = isRootsBridgeDomain(runtimeHostname);
  const initialPage = standaloneEntryPage || saved.page || "home";

  const [lang, setLang] = useState(initialLang);
  const [page, setPage] = useState(initialPage);
  const [, setEchoPlayerState] = useState({
    hasPlayableContent: false,
    isPlaying: false,
    isPaused: false,
    playButtonLabel: "Play",
    bookTitle: "",
    coverImage: "",
    chapterTitle: "",
    status: "",
    progressPercent: 0,
    progressText: "0:00 / 0:00",
    elapsedSeconds: 0,
    totalSeconds: 0
  });
  const [echoUiState, setEchoUiState] = useState({
    book: null,
    shelfBooks: [],
    chapterTitle: "",
    progress: 0,
    currentTime: 0,
    duration: 0,
    isPlaying: false,
    canShowMiniPlayer: false,
    resumePending: false,
    resumeLabel: "",
    resumeNote: ""
  });
  const [echoActions, setEchoActions] = useState({
    togglePlay: null,
    skipBack: null,
    skipForward: null,
    seekToPercent: null,
    openSavedBookAndPlay: null,
    openEcho: null
  });
  const [pendingEchoSelectionImport, setPendingEchoSelectionImport] = useState(null);
  const [isHomeMiniPlayerMinimized, setIsHomeMiniPlayerMinimized] = useState(false);
  const [isWonderMiniPlayerMinimized, setIsWonderMiniPlayerMinimized] = useState(false);
  const echoPlayerRef = useRef(null);
  const echoIsPlayingRef = useRef(false);
  const echoTogglePlayAction = echoActions.togglePlay;
  const zhBrandTerms = new Set([
    "PageTone",
    "R&B Studio",
    "Wonder World",
    "Roots & Bridge",
    "Wonder Bridge",
    "Lumi",
    "Neon Harbor"
  ]);

  const translateZhText = (text) =>
    typeof text === "string"
      ? zhBrandTerms.has(text)
        ? text
        : APP_ZH_TEXT[text] || text
      : text;

  const t = (key, vars = {}) => {
    const template =
      uiCopy[lang]?.[key] ??
      uiCopy.en[key] ??
      I18N[lang]?.[key] ??
      I18N.en[key] ??
      key;
    const localizedTemplate = lang === "zh" ? translateZhText(template) : template;
    return Object.entries(vars).reduce(
      (acc, [k, value]) => acc.replace(new RegExp(`\\{${k}\\}`, "g"), String(value)),
      localizedTemplate
    );
  };
  const btn = (key) => {
    const label = uiButtons[lang]?.[key] ?? uiButtons.en[key] ?? key;
    return lang === "zh" ? translateZhText(label) : label;
  };
  const pickInline = (englishText, frenchText) =>
    lang === "fr" ? frenchText : localizeText(englishText);

  const localizeText = (text) => {
    if (typeof text !== "string") {
      return text;
    }

    if (lang === "en") {
      return text;
    }

    if (lang === "zh") {
      return translateZhText(text);
    }

    return NEW_COPY_FR[text] || CURATED_FR_TEXT[text] || autoFrenchFallback(text);
  };

  const renderWonderDisplayText = (text, options = {}) => {
    const {
      as: Tag = "span",
      className = "",
      variant = "section",
      title
    } = options;

    if (typeof text !== "string") {
      return <Tag className={className}>{text}</Tag>;
    }

    return (
      <WonderGlyphText
        as={Tag}
        text={text}
        variant={variant}
        className={className}
        title={title}
      />
    );
  };

  const getWorldDisplayName = (worldId) => {
    if (!worldId) return "";
    if (lang === "zh") {
      return localizeText(worldMeta[worldId]?.name?.en || worldId);
    }
    return worldMeta[worldId]?.name?.[lang] || worldMeta[worldId]?.name?.en || worldId;
  };
  const aboutContent = lang === "zh" ? ABOUT_CONTENT.en : ABOUT_CONTENT[lang] || ABOUT_CONTENT.en;
  const ww = useWonderWorld({
    lang,
    page,
    setPage,
    t,
    pickInline,
    localizeText
  });
  const {
    world,
    setWorld,
    node,
    setNode,
    completed,
    unlockedWorlds,
    question,
    setQuestion,
    message,
    setMessage,
    stars,
    setScore,
    setDifficulty,
    sessionStart,
    history,
    lastQuestionType,
    setLastQuestionType,
    setAskedQuestionKeys,
    feedbackChoice,
    setFeedbackChoice,
    feedbackNote,
    setFeedbackNote,
    lastAnswerCorrect,
    setLastAnswerCorrect,
    lastResultKind,
    setLastResultKind,
    showClearConfirm,
    setShowClearConfirm,
    showFinalEndingAfterSummary,
    setShowFinalEndingAfterSummary,
    levelState,
    setLevelState,
    setPendingBossQueue,
    usedQuestionChoiceKeys,
    setUsedQuestionChoiceKeys,
    setPendingStoryNode,
    activeNode,
    choose,
    answer,
    finishLevel,
    handleArchiveAndClear: handleWonderArchiveAndClear,
    handleClearWithoutArchive
  } = ww;
  const {
    liveBridgeSender,
    liveBridgeDraft,
    liveBridgeChineseDraft,
    liveBridgeInputStatus,
    liveBridgeSpeechSupported,
    liveBridgeRecognitionLanguage,
    liveBridgeSpeechHint,
    liveBridgeSending,
    liveBridgeTranslating,
    liveBridgeSendError,
    liveBridgeSendSuccess,
    liveBridgeMode,
    liveBridgeViewerPasswordInput,
    liveBridgeViewerPassword,
    liveBridgeMessages,
    liveBridgeViewerLoading,
    liveBridgeViewerError,
    liveBridgeConnectionLabel,
    liveBridgeLatestEnglish,
    liveBridgeLatestChinese,
    liveBridgeUpdatedLabel,
    liveBridgePreview,
    liveBridgeRecordingLabel,
    liveBridgeViewerFeedRef,
    setLiveBridgeSender,
    setLiveBridgeDraft,
    setLiveBridgeChineseDraft,
    setLiveBridgeMode,
    setLiveBridgeViewerPasswordInput,
    handleLiveBridgeSend,
    handleLiveBridgeFillMissingSide,
    handleLiveBridgeSendTestMessage,
    handleLiveBridgeStartNewSession,
    handleLiveBridgeUnlock,
    handleLiveBridgeLock,
    handleLiveBridgeStartListening,
    handleLiveBridgeStopListening,
    handleLiveBridgeClearDrafts
  } = useLiveBridge({ page, t });

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [message, setMessage]);

  useEffect(() => {
    document.body.className =
      page === "home"
        ? "home"
        : page === "readerLab"
        ? "reader-lab"
        : page === "about"
        ? "about"
        : page === "wonderHome"
        ? "wonder-home"
        : page === "liveBridge" || page === "liveBridgeInput" || page === "liveBridgeViewer"
        ? "livebridge"
        : page === "epubTranslator"
        ? "epub-translator"
        : page === "kid"
        ? "kid"
        : page === "parent"
        ? "parent"
        : world || "home";
    document.body.dataset.lang = lang;
  }, [page, world, lang]);

  useEffect(() => {
    if (!standaloneProductKey || !standaloneEntryPage) {
      return;
    }

    if (!pageBelongsToStandaloneProduct(page, standaloneProductKey)) {
      setPage(standaloneEntryPage);
    }
  }, [page, standaloneEntryPage, standaloneProductKey]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const readPendingSelectionFromSession = () => {
      try {
        const rawValue = window.sessionStorage.getItem("ECHO_EXTENSION_PENDING_SELECTION");

        if (!rawValue) {
          return null;
        }

        return JSON.parse(rawValue);
      } catch (error) {
        return null;
      }
    };

    const clearPendingSelectionFromSession = () => {
      try {
        window.sessionStorage.removeItem("ECHO_EXTENSION_PENDING_SELECTION");
      } catch (error) {
        // Ignore storage cleanup failures.
      }
    };

    const queuePendingSelection = (payload) => {
      if (!payload || typeof payload.text !== "string" || !payload.text.trim()) {
        return;
      }

      setPendingEchoSelectionImport({
        ...payload,
        receivedAt: Date.now()
      });
      setIsHomeMiniPlayerMinimized(false);
      setIsWonderMiniPlayerMinimized(false);
      setPage("readerLab");
      clearPendingSelectionFromSession();
    };

    const handleEchoExtensionImport = (event) => {
      if (event.origin !== window.location.origin) {
        return;
      }

      if (event.data?.type !== "ECHO_EXTENSION_IMPORT_SELECTION") {
        return;
      }

      queuePendingSelection(event.data.payload);
    };

    queuePendingSelection(readPendingSelectionFromSession());
    window.addEventListener("message", handleEchoExtensionImport);

    return () => {
      window.removeEventListener("message", handleEchoExtensionImport);
    };
  }, []);

  useEffect(() => {
    if (!pendingEchoSelectionImport || page !== "readerLab") {
      return;
    }

    const imported = echoPlayerRef.current?.importExternalText?.(pendingEchoSelectionImport);

    if (imported) {
      setPendingEchoSelectionImport(null);
    }
  }, [page, pendingEchoSelectionImport]);

  useEffect(() => {
    if (!echoUiState.canShowMiniPlayer) {
      setIsHomeMiniPlayerMinimized(false);
      setIsWonderMiniPlayerMinimized(false);
    }
  }, [echoUiState.canShowMiniPlayer]);

  useEffect(() => {
    if (!echoUiState.canShowMiniPlayer) {
      return;
    }

    if (page === "home") {
      setIsWonderMiniPlayerMinimized(false);
      return;
    }

    if (page !== "readerLab") {
      setIsHomeMiniPlayerMinimized(false);
      setIsWonderMiniPlayerMinimized(true);
    }
  }, [page, echoUiState.canShowMiniPlayer]);

  useEffect(() => {
    echoIsPlayingRef.current = Boolean(echoUiState.isPlaying);
  }, [echoUiState.isPlaying]);

  useEffect(() => {
    const isWonderWorldContentPage = page !== "home" && page !== "readerLab";

    if (!isWonderWorldContentPage) {
      return;
    }

    if (!echoIsPlayingRef.current) {
      return;
    }

    if (echoTogglePlayAction) {
      echoTogglePlayAction();
      return;
    }

    echoPlayerRef.current?.togglePlayback?.();
  }, [page, echoTogglePlayAction]);

  // Guard invalid persisted state to reduce blank-screen cases after refresh/corrupt cache.
  useEffect(() => {
    const worldExists = world && worlds[world];

    if (world && !worldExists) {
      setWorld(null);
      setNode("start");
      setQuestion(null);
      setPendingBossQueue([]);
      setLastAnswerCorrect(null);
      setPage("kid");
      return;
    }

    if (page === "story" && worldExists && !worlds[world][node]) {
      setNode("start");
      return;
    }

    if ((page === "story" || page === "question" || page === "result" || page === "summary") && !worldExists) {
      setPage("kid");
    }
  }, [
    node,
    page,
    setLastAnswerCorrect,
    setNode,
    setPage,
    setPendingBossQueue,
    setQuestion,
    setWorld,
    world
  ]);

  function startWorld(w) {
    if (!unlockedWorlds.includes(w)) {
      return;
    }
    setWorld(w);
    setNode("start");
    setLevelState(1);
    setScore(0);
    setDifficulty("easy");
    setLastQuestionType(null);
    setAskedQuestionKeys([]);
    setUsedQuestionChoiceKeys([]);
    setQuestion(null);
    setLastAnswerCorrect(null);
    setLastResultKind("normal");
    setPendingBossQueue([]);
    setPendingStoryNode(null);
    setShowFinalEndingAfterSummary(false);
    setPage("story");
  }

  function goHome() {
    setShowClearConfirm(false);
    setShowFinalEndingAfterSummary(false);
    setPage(standaloneEntryPage || "home");
  }

  function goToMap() {
    if (world && worlds[world]) {
      setNode("start");
    }
    setQuestion(null);
    setPendingBossQueue([]);
    setPendingStoryNode(null);
    setLastAnswerCorrect(null);
    setLastResultKind("normal");
    setShowFinalEndingAfterSummary(false);
    setPage("kid");
  }

  function handleEchoPlayToggle() {
    if (echoActions.togglePlay) {
      echoActions.togglePlay();
      return;
    }

    if (echoPlayerRef.current) {
      echoPlayerRef.current.togglePlayback();
    }
  }

  function handleEchoOpen() {
    setPage("readerLab");
  }

  function handleEchoSkipBack() {
    if (echoActions.skipBack) {
      echoActions.skipBack();
      return;
    }

    if (echoPlayerRef.current?.skipBackBySeconds) {
      echoPlayerRef.current.skipBackBySeconds(15);
    }
  }

  function handleEchoSkipForward() {
    if (echoActions.skipForward) {
      echoActions.skipForward();
      return;
    }

    if (echoPlayerRef.current?.skipForwardBySeconds) {
      echoPlayerRef.current.skipForwardBySeconds(15);
    }
  }

  function handleEchoSeekToPercent(nextPercent) {
    if (echoActions.seekToPercent) {
      echoActions.seekToPercent(nextPercent);
      return;
    }

    if (echoPlayerRef.current?.seekToPercent) {
      echoPlayerRef.current.seekToPercent(nextPercent);
    }
  }

  const handleEchoUiStateChange = useCallback((nextUiState) => {
    setEchoUiState(nextUiState);
  }, []);

  const handleRegisterEchoActions = useCallback((nextActions) => {
    setEchoActions((current) => ({
      ...current,
      ...nextActions,
      openEcho: () => setPage("readerLab")
    }));
  }, []);

  function handleOpenSavedBookFromShelf(bookId) {
    if (!bookId) {
      return;
    }

    if (echoActions.openSavedBookAndPlay) {
      echoActions.openSavedBookAndPlay(bookId);
      setPage("readerLab");
      return;
    }

    if (echoPlayerRef.current?.openSavedBookAndPlay) {
      echoPlayerRef.current.openSavedBookAndPlay(bookId);
      setPage("readerLab");
    }
  }

  function saveFeedback(type, value, note = "") {
    const existing = readJsonFromStorage("wonderworld_feedback", []);
    const data = Array.isArray(existing) ? existing : [];
    data.push({
      type,
      value,
      note,
      world: world || "",
      lang,
      time: Date.now()
    });
    localStorage.setItem("wonderworld_feedback", JSON.stringify(data));
  }

  function submitHomeFeedback() {
    const trimmedNote = feedbackNote.trim();
    if (!feedbackChoice && !trimmedNote) {
      setMessage(t("feedback_pick_or_type"));
      return;
    }

    saveFeedback("home_feedback", feedbackChoice || "no-option", trimmedNote);

    const emailSubject = encodeURIComponent("Wonder World Feedback");
    const emailBody = encodeURIComponent(
      [
        "Wonder World Feedback",
        `Time: ${new Date().toLocaleString()}`,
        `Language: ${lang.toUpperCase()}`,
        `Selected option: ${feedbackChoice || "no-option"}`,
        `Message: ${trimmedNote || "(empty)"}`,
        `World context: ${world || "none"}`
      ].join("\n")
    );

    window.location.href = `mailto:rootsandbridge@outlook.com?subject=${emailSubject}&body=${emailBody}`;
    setMessage(t("feedback_mail_opened"));
    setFeedbackChoice("");
    setFeedbackNote("");
    goHome();
  }

  function closeClearDialogWithoutChanges() {
    setShowClearConfirm(false);
  }

  function handleArchiveAndClear() {
    downloadReportTxt({ history, lang, t, pickInline, getWorldDisplayName });
    handleWonderArchiveAndClear();
  }

  const activeChapterCopy = world ? chapterCopy[world] : null;
  const activeBossCopy = world ? bossCopy[world] : null;
  const activeBossMiniCopy = world ? bossMiniCopy[world] : null;
  const wonderWorldViewProps = {
    page,
    showClearConfirm,
    lang,
    t,
    worldOrder,
    worldMeta,
    chapterIntroCopy,
    worldCardCopy,
    chapterCopy,
    world,
    node,
    completed,
    unlockedWorlds,
    question,
    stars,
    history,
    sessionStart,
    levelState,
    activeNode,
    activeChapterCopy,
    activeBossCopy,
    activeBossMiniCopy,
    feedbackChoice,
    feedbackNote,
    lastQuestionType,
    lastResultKind,
    lastAnswerCorrect,
    showFinalEndingAfterSummary,
    usedQuestionChoiceKeys,
    localizeText,
    pickInline,
    renderWonderDisplayText,
    getWorldDisplayName,
    getQuestionChoiceKey,
    getCognitiveType,
    getCognitiveTypeLabel,
    generateSummary,
    generateTrendReport,
    startWorld,
    choose,
    answer,
    finishLevel,
    goHome,
    goToMap,
    submitHomeFeedback,
    handleArchiveAndClear,
    handleClearWithoutArchive,
    closeClearDialogWithoutChanges,
    setPage,
    setFeedbackChoice,
    setFeedbackNote,
    setShowClearConfirm,
    setShowFinalEndingAfterSummary
  };
  const showEcho = page === "readerLab";
  const showEpubTranslator = page === "epubTranslator";
  const isWonderWorldContentPage = page !== "home" && page !== "readerLab" && page !== "epubTranslator";
  const showMapQuickButton = ["story", "question", "result", "summary", "finalEnding"].includes(page);
  const showEchoMiniPlayer = !showEcho && echoUiState.canShowMiniPlayer;
  const showHomeEmbeddedEchoMiniPlayer = page === "home" && showEchoMiniPlayer;
  const showExpandedEchoMiniPlayer =
    showEchoMiniPlayer &&
    !(page === "home" && isHomeMiniPlayerMinimized) &&
    !(isWonderWorldContentPage && isWonderMiniPlayerMinimized);
  const showGlobalEchoMiniPlayer = showExpandedEchoMiniPlayer && page !== "home";
  const showWonderMiniPlayerDock =
    isWonderWorldContentPage && showEchoMiniPlayer && isWonderMiniPlayerMinimized;
  const usesTopMiniPlayer = true;
  const homeEchoShelfBooks = Array.isArray(echoUiState.shelfBooks)
    ? echoUiState.shelfBooks.slice(0, 6)
    : [];
  const homeEchoShelfSlots = Array.from(
    { length: 6 },
    (_, index) => homeEchoShelfBooks[index] || null
  );
  function openStandaloneProduct(productKey, fallbackPage) {
    const targetUrl = STANDALONE_PRODUCT_URLS[productKey];

    if (
      typeof window !== "undefined" &&
      targetUrl &&
      standaloneProductKey !== productKey &&
      canUseStandaloneProductUrls
    ) {
      window.location.assign(targetUrl);
      return;
    }

    setPage(fallbackPage);
  }

  const homeHubCards = [
    {
      id: "wonder-world",
      alias: "幻世界",
      title: "Wonder World",
      description: t("home_hub_world_desc"),
      actionLabel: t("home_hub_world_cta"),
      onClick: () => openStandaloneProduct("wonderworld", "wonderHome"),
      toneClass: "is-wonder"
    },
    {
      id: "pagetone",
      alias: "页音",
      title: "PageTone",
      description: t("home_hub_pagetone_desc"),
      actionLabel: t("home_hub_pagetone_cta"),
      onClick: () => openStandaloneProduct("pagetone", "readerLab"),
      toneClass: "is-pagetone"
    },
    {
      id: "epub-translator",
      alias: "书译",
      title: "EPUB Translator",
      description: "Use your monthly DeepL quota to turn one English EPUB slice into a Chinese EPUB.",
      actionLabel: "Open translator",
      onClick: () => setPage("epubTranslator"),
      toneClass: "is-epub"
    },
    {
      id: "livebridge",
      alias: "瞬言",
      title: "LiveBridge",
      description: t("home_hub_livebridge_desc"),
      actionLabel: t("home_hub_livebridge_cta"),
      onClick: () => openStandaloneProduct("livebridge", "liveBridge"),
      toneClass: "is-livebridge"
    },
    {
      id: "coming-soon",
      alias: t("home_hub_coming_alias"),
      title: "Coming soooon",
      description: t("home_hub_coming_desc"),
      actionLabel: "",
      onClick: null,
      toneClass: "is-coming",
      hasAction: false
    }
  ];

  return (
    <div
      className={`app ${page === "home" ? "app--home" : ""} ${
        page === "home" && showGlobalEchoMiniPlayer ? "app--with-echo-mini" : ""
      } ${showGlobalEchoMiniPlayer && usesTopMiniPlayer ? "app--with-top-echo-mini" : ""} ${
        showWonderMiniPlayerDock ? "app--with-wonder-echo-dock" : ""
      }`}
    >
      <div
        className={`langSwitch langTopRight ${
          page !== "home" && page !== "readerLab" && page !== "epubTranslator" ? "withHome" : "solo"
        } ${showWonderMiniPlayerDock ? "withEchoDock" : ""}`}
      >
        <button
          className={`langButton ${lang === "en" ? "active" : ""}`}
          onClick={() => setLang("en")}
        >
          EN
        </button>
        <button
          className={`langButton ${lang === "fr" ? "active" : ""}`}
          onClick={() => setLang("fr")}
        >
          FR
        </button>
        <button
          className={`langButton ${lang === "zh" ? "active" : ""}`}
          onClick={() => setLang("zh")}
        >
          中文
        </button>
      </div>

      {page !== "home" && page !== "readerLab" && page !== "epubTranslator" && (
        <button
          className="navButton"
          onClick={goHome}
          aria-label={btn("home")}
          title={btn("home")}
        >
          🏠
        </button>
      )}

      {showWonderMiniPlayerDock && (
        <button
          className="wonderEchoMiniDockButton"
          type="button"
          aria-label="Show PageTone mini player"
          title={echoUiState.book?.title || "Show PageTone mini player"}
          onClick={() => setIsWonderMiniPlayerMinimized(false)}
        >
          {echoUiState.book?.cover ? (
            <img
              className="wonderEchoMiniDockImage"
              src={echoUiState.book.cover}
              alt={echoUiState.book.title || "Current book"}
            />
          ) : (
            <div className="wonderEchoMiniDockImage wonderEchoMiniDockFallback">
              {((echoUiState.book?.title || "E").trim().slice(0, 1) || "E").toUpperCase()}
            </div>
          )}
        </button>
      )}

      {showMapQuickButton && (
        <button
          className="mapNavButton"
          onClick={goToMap}
          aria-label={btn("map")}
          title={btn("map")}
        >
          🗺 {btn("map")}
        </button>
      )}

      <div className="globalSiteBrand">
        <div className="globalSiteTitle">R&amp;B Studio</div>
        <div className="globalSiteSubtitle">Roots &amp; Bridge</div>
      </div>
      {page === "readerLab" && (
        <>
          <div className="globalEchoBrandLockup">
            <div className="globalEchoTitle">PageTone</div>
            <div className="globalEchoTagline">{t("echo_tagline")}</div>
          </div>
        </>
      )}

      {page === "home" && (
        <div className="homeHubLayout" aria-label={t("home_aria")}>
          <div className="homeHubGrid">
            {homeHubCards.map((card) => (
              <section
                key={card.id}
                className={`homeHubCard ${card.toneClass}`}
                aria-labelledby={`home-hub-title-${card.id}`}
              >
                <div className="homeHubCardHead">
                  <h2 className="homeHubTitle" id={`home-hub-title-${card.id}`}>
                    <span className="homeHubTitleMain">{card.title}</span>
                    {card.alias ? <span className="homeHubTitleAlias">{card.alias}</span> : null}
                  </h2>
                </div>

                <p className="homeHubDescription">{card.description}</p>

                {card.id === "pagetone" ? (
                  <div className="homeHubPageToneStack">
                    <button
                      className="homeHubPageToneUploadCue"
                      type="button"
                      onClick={card.onClick}
                      aria-label={t("home_hub_pagetone_upload_title")}
                    >
                      <span className="homeHubPageToneUploadCue__plus" aria-hidden="true">
                        ＋
                      </span>
                      <span className="homeHubPageToneUploadCue__text">
                        <span className="homeHubPageToneUploadCue__title">
                          {t("home_hub_pagetone_upload_title")}
                        </span>
                        <span className="homeHubPageToneUploadCue__hint">
                          {t("home_hub_pagetone_upload_hint")}
                        </span>
                      </span>
                    </button>

                    {showHomeEmbeddedEchoMiniPlayer ? (
                      <EchoMiniPlayer
                        visible={showHomeEmbeddedEchoMiniPlayer}
                        placement="embedded"
                        book={echoUiState.book}
                        chapterTitle={echoUiState.chapterTitle}
                        resumePending={echoUiState.resumePending}
                        resumeLabel={echoUiState.resumeLabel}
                        resumeNote={echoUiState.resumeNote}
                        progress={echoUiState.progress}
                        currentTime={echoUiState.currentTime}
                        duration={echoUiState.duration}
                        isPlaying={echoUiState.isPlaying}
                        onTogglePlay={echoActions.togglePlay || handleEchoPlayToggle}
                        onOpenEcho={echoActions.openEcho || handleEchoOpen}
                        onSeekPercent={echoActions.seekToPercent || handleEchoSeekToPercent}
                        onSkipBack={echoActions.skipBack || handleEchoSkipBack}
                        onSkipForward={echoActions.skipForward || handleEchoSkipForward}
                        minimizable={false}
                      />
                    ) : null}

                    <div className="homeHubShelfPreview">
                      <div className="homeHubShelfRow">
                        {homeEchoShelfSlots.map((book, index) =>
                          book ? (
                            <button
                              key={book.id || `home-hub-shelf-${index}`}
                              className="homeHubShelfBook"
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleOpenSavedBookFromShelf(book.id);
                              }}
                              aria-label={`Resume: ${book.title || "Saved book"}`}
                              title={book.title || "Saved book"}
                            >
                              {book.coverImage ? (
                                <img
                                  className="homeHubShelfBookImage"
                                  src={book.coverImage}
                                  alt={book.title || "Saved book"}
                                />
                              ) : (
                                <div className="homeHubShelfBookFallback">
                                  {(book.title || "P").trim().slice(0, 1).toUpperCase()}
                                </div>
                              )}
                            </button>
                          ) : (
                            <div
                              key={`home-hub-shelf-${index}`}
                              className="homeHubShelfBook is-empty"
                              aria-hidden="true"
                            />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                ) : null}

                {card.id === "wonder-world" ? (
                  <div className="homeHubWonderPreview" aria-hidden="true">
                    <div className="homeGameArtPreviewImage homeHubWonderPreviewImage" />
                  </div>
                ) : null}

                {card.hasAction !== false ? (
                  <div className="homeHubActionRow">
                    <button className="homeHubActionButton" onClick={card.onClick}>
                      {card.actionLabel}
                    </button>
                  </div>
                ) : null}
              </section>
            ))}
          </div>
        </div>
      )}

      {page === "about" && (
        <div className="aboutPage">
          <div className="aboutCard">
            <div className="aboutHeader">
              <h2 className="aboutTitle">{t("about_title")}</h2>

              <button className="aboutBackButton" onClick={() => setPage("wonderHome")}>
                {t("about_back")}
              </button>
            </div>

            <div className="aboutBody">
              {aboutContent.paragraphs.map((paragraph, idx) => (
                <p key={`about-paragraph-${idx}`}>{lang === "zh" ? localizeText(paragraph) : paragraph}</p>
              ))}

              <p className="aboutPrivacy">
                <strong>{pickInline("A note on privacy:", "Note sur la confidentialité :")}</strong>{" "}
                {lang === "zh"
                  ? localizeText(aboutContent.privacy).replace(/^.*?[：:]\s*/, "")
                  : aboutContent.privacy.replace(/^.*?:\s*/, "")}
              </p>

              <p className="aboutGentle">{lang === "zh" ? localizeText(aboutContent.gentle) : aboutContent.gentle}</p>

              <div className="aboutSignature">
                <p>Brian Li</p>
                <p>2026</p>
              </div>
            </div>
          </div>
          <button className="parentDockButton" onClick={() => setPage("parent")}>
            {t("about_view_report")}
          </button>
        </div>
      )}

      {page === "liveBridge" && (
        <div className="aboutPage liveBridgePage">
          <div className="aboutCard liveBridgeCard">
            <div className="aboutHeader">
              <div>
                <div className="liveBridgeAlias">{t("livebridge_alias")}</div>
                <h2 className="aboutTitle">{t("livebridge_title")}</h2>
              </div>
              <button className="aboutBackButton" onClick={goHome}>
                {t("back_home")}
              </button>
            </div>

            <div className="aboutBody liveBridgeBody">
              <div className="liveBridgeHero">
                <p className="liveBridgeTagline">{t("livebridge_tagline")}</p>
                <p>{t("livebridge_short_desc")}</p>
              </div>
              <section className="liveBridgeModeGuide" aria-label="LiveBridge mode guide">
                <p className="liveBridgeModeGuideIntro">{t("livebridge_mode_intro")}</p>
                <div className="liveBridgeModeGuideGrid">
                  <article className="liveBridgeModeGuideCard">
                    <div className="liveBridgeModeGuideLabel">{t("livebridge_mode_live")}</div>
                    <p className="liveBridgeModeGuideText">{t("livebridge_mode_live_note")}</p>
                  </article>
                  <article className="liveBridgeModeGuideCard">
                    <div className="liveBridgeModeGuideLabel">{t("livebridge_mode_organize")}</div>
                    <p className="liveBridgeModeGuideText">{t("livebridge_mode_organize_note")}</p>
                  </article>
                </div>
              </section>
              <div className="liveBridgeModeSwitch" role="tablist" aria-label="LiveBridge mode">
                <button
                  type="button"
                  role="tab"
                  className={`homeSecondaryButton liveBridgeModeButton liveBridgeButtonLiveAlt${
                    liveBridgeMode === "live" ? " active" : ""
                  }`}
                  aria-selected={liveBridgeMode === "live"}
                  onClick={() => setLiveBridgeMode("live")}
                >
                  {t("livebridge_mode_live")}
                </button>
                <button
                  type="button"
                  role="tab"
                  className={`homeSecondaryButton liveBridgeModeButton liveBridgeButtonOrganize${
                    liveBridgeMode === "organize" ? " active" : ""
                  }`}
                  aria-selected={liveBridgeMode === "organize"}
                  onClick={() => setLiveBridgeMode("organize")}
                >
                  {t("livebridge_mode_organize")}
                </button>
              </div>

              {liveBridgeMode === "organize" ? (
                <LiveBridgeOrganize t={t} />
              ) : (
                <>
                  <div className="liveBridgeActionRow">
                    <button className="primaryButton liveBridgeButtonLive" onClick={() => setPage("liveBridgeInput")}>
                      {t("livebridge_try_cta")}
                    </button>
                    <button
                      className="homeSecondaryButton liveBridgeButtonViewer"
                      onClick={() => setPage("liveBridgeViewer")}
                    >
                      {t("livebridge_viewer_cta")}
                    </button>
                  </div>
                  <section className="liveBridgeSection">
                    <h3 className="liveBridgeSectionTitle">{t("livebridge_how_title")}</h3>
                    <div className="liveBridgeStepsGrid">
                      <article className="liveBridgeStepCard">
                        <h4>{t("livebridge_step_speak")}</h4>
                      </article>
                      <article className="liveBridgeStepCard">
                        <h4>{t("livebridge_step_translate")}</h4>
                      </article>
                      <article className="liveBridgeStepCard">
                        <h4>{t("livebridge_step_view")}</h4>
                      </article>
                    </div>
                  </section>
                  <section className="liveBridgeSection">
                    <h3 className="liveBridgeSectionTitle">{t("livebridge_usecase_title")}</h3>
                    <p>{t("livebridge_usecase_body")}</p>
                  </section>
                  <section className="liveBridgeSection">
                    <h3 className="liveBridgeSectionTitle">{t("livebridge_access_title")}</h3>
                    <div className="liveBridgeActionRow">
                      <button
                        className="primaryButton liveBridgeButtonLive"
                        onClick={() => setPage("liveBridgeInput")}
                      >
                        {t("livebridge_input_cta")}
                      </button>
                      <button
                        className="homeSecondaryButton liveBridgeButtonViewer"
                        onClick={() => setPage("liveBridgeViewer")}
                      >
                        {t("livebridge_viewer_cta")}
                      </button>
                    </div>
                  </section>
                  <p>{t("livebridge_intro")}</p>
                  <p>{t("livebridge_note")}</p>
                  <p className="liveBridgeStatus">{t("livebridge_status")}</p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {page === "liveBridgeInput" && (
        <div className="aboutPage liveBridgePage">
          <div className="aboutCard liveBridgeCard lbInputCard">
            <div className="aboutBody liveBridgeBody">
              <div className="lbInputHeader">
                <button className="aboutBackButton" onClick={() => setPage("liveBridge")}>
                  {t("about_back")}
                </button>

                <div className="lbInputHeaderCenter">
                  <div className="liveBridgeAlias">{t("livebridge_alias")}</div>
                  <h2 className="lbInputTitle">{t("livebridge_input_title")}</h2>
                </div>

                <div className="lbInputNameField">
                  <label className="lbInputNameLabel" htmlFor="livebridge-name">
                    {t("livebridge_name_label")}
                  </label>
                  <input
                    id="livebridge-name"
                    className="liveBridgeFieldInput lbInputNameInput"
                    type="text"
                    value={liveBridgeSender}
                    onChange={(event) => setLiveBridgeSender(event.target.value)}
                    placeholder={t("livebridge_name_placeholder")}
                    maxLength={40}
                  />
                </div>
              </div>

              <section className="lbLivePreview" aria-label={t("livebridge_preview_title")}>
                <div className="lbLivePreviewHeader">
                  <span className="liveBridgeSectionTitle">{t("livebridge_preview_title")}</span>
                  <span className={`lbLiveBadge${liveBridgeInputStatus === "listening" ? " is-live" : ""}`}>
                    {liveBridgeInputStatus === "listening"
                      ? t("livebridge_preview_badge_live")
                      : t("livebridge_preview_badge_ready")}
                  </span>
                  <span className="lbConnectionDot" title={liveBridgeConnectionLabel} />
                </div>
                <div className="lbLivePreviewBody">
                  <p className="liveBridgeViewerChineseText lbPreviewZh">
                    {liveBridgePreview?.chineseText ||
                      liveBridgeChineseDraft ||
                      liveBridgeLatestChinese ||
                      "—"}
                  </p>
                  <p className="liveBridgeViewerEnglishText lbPreviewEn">
                    {liveBridgePreview?.englishText ||
                      liveBridgeDraft ||
                      liveBridgeLatestEnglish ||
                      "—"}
                  </p>
                </div>
              </section>

              <div className="lbRecordZone">
                <div className="lbRecordButtons">
                  <button
                    className="primaryButton liveBridgeButtonLive lbRecordBtn"
                    onClick={() => handleLiveBridgeStartListening("en-US")}
                    disabled={liveBridgeInputStatus === "listening" || !liveBridgeSpeechSupported}
                  >
                    {t("livebridge_start_listening_en")}
                  </button>
                  <button
                    className="homeSecondaryButton liveBridgeButtonLiveAlt lbRecordBtn"
                    onClick={() => handleLiveBridgeStartListening("zh-CN")}
                    disabled={liveBridgeInputStatus === "listening" || !liveBridgeSpeechSupported}
                  >
                    {t("livebridge_start_listening_zh")}
                  </button>
                  <button
                    className="homeSecondaryButton liveBridgeButtonStop lbRecordBtn"
                    onClick={handleLiveBridgeStopListening}
                    disabled={liveBridgeInputStatus !== "listening"}
                  >
                    {t("livebridge_stop_listening")}
                  </button>
                </div>
                <div className="lbRecordMeta">
                  {liveBridgeInputStatus === "listening" ? (
                    <span className="lbTimerPill is-active">
                      {liveBridgeRecognitionLanguage.startsWith("zh")
                        ? t("livebridge_listening_chinese")
                        : t("livebridge_listening_english")}
                      <span className="lbTimerValue">{liveBridgeRecordingLabel}</span>
                    </span>
                  ) : (
                    <span className="lbTimerPill">
                      {liveBridgeSpeechHint ||
                        (liveBridgeSpeechSupported
                          ? t("livebridge_speech_ready")
                          : t("livebridge_speech_note"))}
                    </span>
                  )}
                </div>
              </div>

              <div className="lbDraftGrid">
                <div className="lbDraftCol">
                  <label className="liveBridgeFieldLabel" htmlFor="livebridge-message">
                    {t("livebridge_message_label")}
                  </label>
                  <textarea
                    id="livebridge-message"
                    className="liveBridgeFieldTextarea lbDraftTextarea"
                    value={liveBridgeDraft}
                    onChange={(event) => setLiveBridgeDraft(event.target.value)}
                    placeholder={t("livebridge_message_placeholder")}
                    rows={6}
                    maxLength={1200}
                  />
                </div>

                <div className="lbDraftCol">
                  <label className="liveBridgeFieldLabel" htmlFor="livebridge-chinese-message">
                    {t("livebridge_chinese_label")}
                  </label>
                  <textarea
                    id="livebridge-chinese-message"
                    className="liveBridgeFieldTextarea lbDraftTextarea"
                    value={liveBridgeChineseDraft}
                    onChange={(event) => setLiveBridgeChineseDraft(event.target.value)}
                    placeholder={t("livebridge_chinese_placeholder")}
                    rows={6}
                    maxLength={1200}
                  />
                </div>
              </div>

              {liveBridgeSendError ? (
                <p className="liveBridgeErrorText">{liveBridgeSendError}</p>
              ) : null}
              {liveBridgeSendSuccess ? (
                <p className="liveBridgeSuccessText">{liveBridgeSendSuccess}</p>
              ) : null}

              <div className="lbActionBar">
                <div className="lbActionPrimary">
                  <button
                    className="primaryButton liveBridgeButtonSend"
                    onClick={handleLiveBridgeSend}
                    disabled={
                      (!String(liveBridgeDraft || "").trim() &&
                        !String(liveBridgeChineseDraft || "").trim()) ||
                      liveBridgeSending
                    }
                  >
                    {liveBridgeSending ? t("livebridge_sending") : t("livebridge_send")}
                  </button>
                  <button
                    className="homeSecondaryButton liveBridgeButtonTranslate"
                    onClick={handleLiveBridgeFillMissingSide}
                    disabled={liveBridgeSending || liveBridgeTranslating}
                  >
                    {liveBridgeTranslating
                      ? t("livebridge_sending")
                      : t("livebridge_fill_missing")}
                  </button>
                  <button
                    className="homeSecondaryButton liveBridgeButtonDanger"
                    onClick={handleLiveBridgeClearDrafts}
                  >
                    {t("livebridge_clear")}
                  </button>
                </div>
                <div className="lbActionUtility">
                  <button
                    className="homeSecondaryButton liveBridgeButtonUtility"
                    onClick={handleLiveBridgeSendTestMessage}
                    disabled={liveBridgeSending}
                  >
                    {t("livebridge_send_test")}
                  </button>
                  <button
                    className="homeSecondaryButton liveBridgeButtonUtility"
                    onClick={handleLiveBridgeStartNewSession}
                    disabled={liveBridgeSending}
                  >
                    {t("livebridge_new_session")}
                  </button>
                </div>
              </div>

              <section className="liveBridgeSection liveBridgeRecentSection">
                <h3 className="liveBridgeSectionTitle">{t("livebridge_recent_lines")}</h3>
                <div className="liveBridgeInputFeed">
                  {liveBridgeMessages.length ? (
                    liveBridgeMessages.map((message) => (
                      <article className="liveBridgeInputMessageCard" key={message.id}>
                        <div className="liveBridgeMessageMeta">
                          <span className="liveBridgeMessageSender">{message.sender || "Guest"}</span>
                          <span className="liveBridgeMessageTime">
                            {message.createdAt
                              ? new Date(message.createdAt).toLocaleTimeString([], {
                                  hour: "numeric",
                                  minute: "2-digit"
                                })
                              : ""}
                          </span>
                        </div>
                        <p className="liveBridgeMessageText">{message.englishText || "—"}</p>
                        <p className="liveBridgeMessageText liveBridgeMessageTextChinese">
                          {message.chineseText || "—"}
                        </p>
                      </article>
                    ))
                  ) : (
                    <div className="liveBridgeViewerEmpty">{t("livebridge_viewer_empty")}</div>
                  )}
                </div>
              </section>
            </div>
          </div>
        </div>
      )}

      {page === "liveBridgeViewer" && (
        <div className="aboutPage liveBridgePage">
          <div className="aboutCard liveBridgeCard">
            <div className="aboutHeader">
              <div>
                <div className="liveBridgeAlias">{t("livebridge_alias")}</div>
                <h2 className="aboutTitle">{t("livebridge_viewer_title")}</h2>
              </div>
              <button className="aboutBackButton" onClick={() => setPage("liveBridge")}>
                {t("about_back")}
              </button>
            </div>

            <div className="aboutBody liveBridgeBody">
              <p>{t("livebridge_viewer_intro")}</p>

              {!liveBridgeViewerPassword ? (
                <>
                  <div className="liveBridgeField">
                    <label className="liveBridgeFieldLabel" htmlFor="livebridge-password">
                      {t("livebridge_password_label")}
                    </label>
                    <input
                      id="livebridge-password"
                      className="liveBridgeFieldInput"
                      type="password"
                      value={liveBridgeViewerPasswordInput}
                      onChange={(event) => setLiveBridgeViewerPasswordInput(event.target.value)}
                      placeholder={t("livebridge_password_placeholder")}
                    />
                  </div>

                  {liveBridgeViewerError ? (
                    <p className="liveBridgeErrorText">{liveBridgeViewerError}</p>
                  ) : null}

                  <div className="liveBridgeActionRow">
                    <button
                      className="primaryButton liveBridgeButtonViewer"
                      onClick={handleLiveBridgeUnlock}
                      disabled={liveBridgeViewerLoading}
                    >
                      {t("livebridge_unlock")}
                    </button>
                  </div>
                  <p className="liveBridgeSessionNote">{t("livebridge_session_note")}</p>
                </>
              ) : (
                <>
                  <div className="liveBridgeStatusRow">
                    <span className="liveBridgeStatus">
                      {t("livebridge_room_label")}: {t("livebridge_room_value")}
                    </span>
                    <span className="liveBridgeStatus">{t("livebridge_status_private")}</span>
                    <span className="liveBridgeStatus">{liveBridgeConnectionLabel}</span>
                    {liveBridgeUpdatedLabel ? (
                      <span className="liveBridgeStatus">
                        {t("livebridge_last_updated")}: {liveBridgeUpdatedLabel}
                      </span>
                    ) : null}
                  </div>

                  {liveBridgeViewerError ? (
                    <p className="liveBridgeErrorText">{liveBridgeViewerError}</p>
                  ) : null}

                  <div
                    className="liveBridgeViewerFeed"
                    role="log"
                    aria-live="polite"
                    ref={liveBridgeViewerFeedRef}
                  >
                    {liveBridgePreview &&
                    (liveBridgePreview.englishText || liveBridgePreview.chineseText) ? (
                      <article className="liveBridgeMessageCard liveBridgeViewerMessageCard liveBridgeViewerPreviewCard">
                        <div className="liveBridgeMessageMeta">
                          <span className="liveBridgeMessageSender">
                            {liveBridgePreview.sender || "Guest"}
                          </span>
                          <span className="liveBridgePreviewBadge">
                            {t("livebridge_preview_badge_live")}
                          </span>
                        </div>
                        <p className="liveBridgeViewerChineseText">
                          {liveBridgePreview.chineseText || "—"}
                        </p>
                        <p className="liveBridgeViewerEnglishText">
                          {liveBridgePreview.englishText || "—"}
                        </p>
                      </article>
                    ) : null}
                    {liveBridgeMessages.length ? (
                      liveBridgeMessages.map((message) => (
                        <article className="liveBridgeMessageCard liveBridgeViewerMessageCard" key={message.id}>
                          <div className="liveBridgeMessageMeta">
                            <span className="liveBridgeMessageSender">{message.sender || "Guest"}</span>
                            <span className="liveBridgeMessageTime">
                              {message.createdAt
                                ? new Date(message.createdAt).toLocaleTimeString([], {
                                    hour: "numeric",
                                    minute: "2-digit"
                                  })
                                : ""}
                            </span>
                          </div>
                          <p className="liveBridgeViewerChineseText">
                            {message.chineseText || "—"}
                          </p>
                          <p className="liveBridgeViewerEnglishText">
                            {message.englishText || "—"}
                          </p>
                        </article>
                      ))
                    ) : (
                      <div className="liveBridgeViewerEmpty">
                        {liveBridgeViewerLoading
                          ? t("livebridge_viewer_waiting")
                          : t("livebridge_viewer_empty")}
                      </div>
                    )}
                  </div>

                  <div className="liveBridgeActionRow">
                    <button className="homeSecondaryButton liveBridgeButtonDanger" onClick={handleLiveBridgeLock}>
                      {t("livebridge_lock")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <WonderWorldViews {...wonderWorldViewProps} />

      {showEpubTranslator && <EpubTranslator onBack={goHome} />}

      <ReaderLab
        ref={echoPlayerRef}
        lang={lang}
        isActive={showEcho}
        visible={showEcho}
        onClose={isStandaloneEchoHost ? undefined : () => setPage("home")}
        onPlayerStateChange={setEchoPlayerState}
        onUiStateChange={handleEchoUiStateChange}
        onRegisterActions={handleRegisterEchoActions}
      />

      <EchoMiniPlayer
        visible={showGlobalEchoMiniPlayer}
        placement={usesTopMiniPlayer ? "top" : "bottom"}
        book={echoUiState.book}
        chapterTitle={echoUiState.chapterTitle}
        resumePending={echoUiState.resumePending}
        resumeLabel={echoUiState.resumeLabel}
        resumeNote={echoUiState.resumeNote}
        progress={echoUiState.progress}
        currentTime={echoUiState.currentTime}
        duration={echoUiState.duration}
        isPlaying={echoUiState.isPlaying}
        onTogglePlay={echoActions.togglePlay || handleEchoPlayToggle}
        onOpenEcho={echoActions.openEcho || handleEchoOpen}
        onSeekPercent={echoActions.seekToPercent || handleEchoSeekToPercent}
        onSkipBack={echoActions.skipBack || handleEchoSkipBack}
        onSkipForward={echoActions.skipForward || handleEchoSkipForward}
        minimizable={isWonderWorldContentPage}
        onMinimize={
          isWonderWorldContentPage
            ? () => setIsWonderMiniPlayerMinimized(true)
            : undefined
        }
      />

      {message && <div className="toast">{message}</div>}
      <a
        className="siteCredit"
        href="https://www.rootsandbridge.com/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open Roots & Bridge"
        title="Roots & Bridge"
      >
        <img className="siteCreditLogo" src={rootsBridgeLogo} alt="Roots & Bridge" />
      </a>
    </div>
  );
}
